/**
 * Mountain West and Southwest craft malthouses — Blacklands Malt, Colorado
 * Malting Company, Grouse Malt House, Montana Craft Malt, Root Shoot
 * Malting, Sinagua Malt, TexMalt, Troubadour Maltings, Mountain Malt and
 * Schwebach Malz.
 */

import type { Fermentable } from '../../../schema/fermentable';
import { craftMalt } from './helpers';

/** Colorado, Montana, Texas, Arizona, Utah, Idaho and New Mexico craft malts. @see Fermentable */
export const mountainWestCraftFermentables: readonly Fermentable[] = [
  // ---------------------------------------------------------------------------
  // Blacklands Malt — Leander, Texas
  //
  // The first malthouse in modern Texas history, opened 2012 and **closed in
  // 2020**; St. Edward's University holds its archive. Rows are kept so that
  // recipes and cupboards that reference them still resolve, and because the
  // malt outlived the malthouse in a lot of Texas recipe books.
  //
  // Every figure below is Blacklands' own, off their product page
  // (`blacklandsmalt.com/malts.php`, the single Wayback capture of it,
  // 2020-08-07 — the domain now serves unrelated content). They published a
  // near-complete analysis per malt: colour, moisture, protein, diastatic
  // power, alpha amylase and fine-grind extract (their "FG"). Colour and
  // extract are theirs unrounded, mid-point where they gave a range, so
  // `provenance: 'spec-sheet'` is earned on the eight malts that page listed.
  //
  // Two things the page does not give us. Blacklands quote colour in **SRM**;
  // the repo stores °L, and for *malt* colour the two are the same ASBC
  // measurement, so the numbers carry across untouched. And they publish no
  // diastatic power for Brown Field 10 or Cold Rock 20 — see those rows.
  //
  // Protein and alpha amylase have no home in the schema and are dropped.
  // ---------------------------------------------------------------------------
  craftMalt({
    // id says `pale`; the product is Pale Moon, their pale *ale* malt. Left
    // alone so stocked inventory keeps matching on `fermentable:blacklands-pale`.
    id: 'blacklands-pale',
    name: 'Pale Moon Texas Pale Ale',
    maker: 'Blacklands Malt',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 82,
    moisturePercent: 3.8,
    colorLovibond: 3.9,
    diastaticPowerLintner: 90,
    suggestedMaxPercent: 100,
    needsMashing: true,
    provenance: 'spec-sheet',
    description:
      'The malt the malthouse opened with, kilned longer and hotter than their pilsners to 3.8–4.0 °L: peanut-butter biscuit, a moderate cereal sweetness and a bready earthiness. Fully self-converting, so it will carry a SMaSH pale ale, an APA or an English bitter on its own.',
    tags: ['base', 'pale-ale', 'ale', 'two-row', 'craft-malt', 'texas', 'local'],
  }),
  craftMalt({
    id: 'blacklands-silver-star',
    name: 'Silver Star Texas 2 Row',
    maker: 'Blacklands Malt',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 83,
    moisturePercent: 4.8,
    colorLovibond: 1.9,
    diastaticPowerLintner: 135,
    suggestedMaxPercent: 100,
    needsMashing: true,
    provenance: 'spec-sheet',
    description:
      'Built for the brewers who could not run a stepped mash for the two under-modified pilsners: low color at 1.8–2.0 °L and 135 °Lintner behind it, so it converts on a single infusion. Clean and neutral with a pronounced grain sweetness, and enzyme to spare for adjunct, light lager and distilling mashes.',
    tags: ['base', 'two-row', 'high-enzyme', 'distilling', 'adjunct', 'craft-malt', 'texas'],
  }),
  craftMalt({
    // id says `pilsner`; White Horn is the original of their two pilsner malts.
    id: 'blacklands-pilsner',
    name: 'White Horn Texas Pilsner',
    maker: 'Blacklands Malt',
    type: 'base',
    maltClass: 'base-pilsner',
    extractFineDryPercent: 82,
    moisturePercent: 5.3,
    colorLovibond: 1.7,
    diastaticPowerLintner: 140,
    suggestedMaxPercent: 100,
    needsMashing: true,
    provenance: 'spec-sheet',
    description:
      'Their first pilsner recipe, from under-modified two-row at 1.6–1.8 °L: raw grain and grass over a clean malt backbone and a mild sweetness. Closer to a European pilsner malt than an American one, and it wants a stepped mash — a single infusion leaves half of it behind.',
    tags: ['base', 'pilsner', 'lager', 'under-modified', 'craft-malt', 'texas', 'local'],
  }),
  craftMalt({
    id: 'blacklands-true-pilsner',
    name: 'True Texas Pilsner',
    maker: 'Blacklands Malt',
    type: 'base',
    maltClass: 'base-pilsner',
    extractFineDryPercent: 83,
    moisturePercent: 5.1,
    colorLovibond: 1.5,
    diastaticPowerLintner: 142,
    suggestedMaxPercent: 100,
    needsMashing: true,
    provenance: 'spec-sheet',
    description:
      'Floor-malted and pushed as far into under-modification as they dared, for the palest wort in the house at 1.5 °L. A decoction malt and only a decoction malt: the enzyme is all there at 142 °Lintner, but an infusion mash will not unlock it. The lager base for brewers who read a certificate of analysis.',
    tags: [
      'base',
      'pilsner',
      'lager',
      'floor-malted',
      'under-modified',
      'decoction',
      'craft-malt',
      'texas',
    ],
  }),
  craftMalt({
    // id says `wheat`; the product is Red Winter, their malted red winter wheat.
    // Their raw grade is `blacklands-raw-wheat` below.
    id: 'blacklands-wheat',
    name: 'Red Winter Texas Wheat',
    maker: 'Blacklands Malt',
    type: 'base',
    maltClass: 'wheat',
    extractFineDryPercent: 81,
    moisturePercent: 5.1,
    colorLovibond: 2.3,
    diastaticPowerLintner: 168,
    suggestedMaxPercent: 80,
    needsMashing: true,
    provenance: 'spec-sheet',
    description:
      'Texas-grown hard red winter wheat kilned cool to keep the enzymes alive, and at 168 °Lintner it out-converts every barley they made. Pale sweet wort with a Grape-Nuts cereal note and a distinct earthiness; foam, body and diastatic backup to 80 % of the grist. Rice hulls at the top of that range.',
    tags: ['base', 'wheat', 'high-enzyme', 'craft-malt', 'texas', 'head-retention', 'local'],
  }),
  craftMalt({
    // Not on the 2020 product page, so no published analysis and no
    // `spec-sheet`: the figures are class-typical for raw wheat. The product is
    // real — Brandon Ade names it in a 2014 interview ("I also offer Raw Red
    // Winter wheat now as well") alongside the malted grade, and it turns up in
    // recipe databases as "Blacklands Raw Wheat".
    id: 'blacklands-raw-wheat',
    name: 'Raw Red Winter Wheat',
    maker: 'Blacklands Malt',
    type: 'adjunct-grain',
    maltClass: 'wheat',
    extractFineDryPercent: 79,
    moisturePercent: 9.5,
    colorLovibond: 2.5,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 40,
    needsMashing: true,
    description:
      'The same Central Texas red winter wheat, sold unmalted: protein for haze and foam, a bready dryness, and no enzyme of its own, so a well-modified base malt has to do the converting. Witbier and lambic want it; expect a sticky lauter and rice hulls past a quarter of the grist.',
    tags: ['adjunct', 'wheat', 'unmalted', 'witbier', 'haze', 'craft-malt', 'texas'],
  }),
  craftMalt({
    // Diastatic power is the one figure Blacklands never published for this
    // malt, so 40 °Lintner is class-typical for a 10 °L Munich rather than
    // theirs; colour, moisture and extract are off their page.
    id: 'blacklands-brown-field-10',
    name: 'Brown Field 10 Texas Munich',
    maker: 'Blacklands Malt',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 78,
    moisturePercent: 4.6,
    colorLovibond: 10,
    diastaticPowerLintner: 40,
    suggestedMaxPercent: 100,
    needsMashing: true,
    provenance: 'spec-sheet',
    description:
      'Six months of kiln work and a steaming step, and a gold medal at the 2020 Malt Cup for it. High-dried and melanoidin-rich off high-nitrogen Texas two-row: rich malt and light toast at 10 °L, for amber, märzen, bock and brown, or a few percent to give a pale beer color and body.',
    tags: ['kilned', 'munich', 'melanoidin', 'amber', 'craft-malt', 'texas', 'award-winner'],
  }),
  craftMalt({
    // Stewed, but self-converting at a published 95 °Lintner, so it is typed
    // `kilned-toasted` rather than `crystal-caramel` — a crystal malt with
    // enzyme in it would be a contradiction the catalog rightly rejects.
    id: 'blacklands-amber-wave',
    name: 'Amber Wave Texas Amber',
    maker: 'Blacklands Malt',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 77,
    moisturePercent: 4.6,
    colorLovibond: 10,
    diastaticPowerLintner: 95,
    suggestedMaxPercent: 100,
    needsMashing: true,
    provenance: 'spec-sheet',
    description:
      'Over-modified high-nitrogen two-row, stewed part-way in the kiln and then dried, which leaves it 95 °Lintner and still able to turn a wort ruby: candy apple, honey graham cracker and a note of cinnamon over a heavy mouthfeel. An amber malt that converts itself, for ambers, Vienna lager and brown ale.',
    tags: ['kilned', 'amber', 'stewed', 'vienna', 'craft-malt', 'texas', 'body'],
  }),
  craftMalt({
    // No diastatic power published, and none expected: it is stewed glassy, so
    // the 0 here is physics rather than a missing figure.
    id: 'blacklands-cold-rock-20',
    name: 'Cold Rock 20 Texas Crystal',
    maker: 'Blacklands Malt',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 75,
    moisturePercent: 4.5,
    colorLovibond: 20,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 50,
    needsMashing: true,
    provenance: 'spec-sheet',
    description:
      'Their first fully glassy crystal malt, stewed by a flat-trap steam method that liquefies the starch to sugar inside the kernel before it is dried out. Toasted marshmallow and caramelized sugar at 20 °L, with the mouthfeel and color a light crystal is bought for.',
    tags: ['crystal', 'caramel', 'light-crystal', 'craft-malt', 'texas', 'body'],
  }),
  craftMalt({
    // The Black Coal series was made to order: any Blacklands malt, cold-smoked
    // over the buyer's choice of mesquite, oak, birch, cherry or apple. Only
    // mesquite gets a row — the woods are options on one product, not five
    // SKUs — and nothing about the series was published as an analysis, hence
    // `representative`. Colour and diastatic power track their base malts,
    // because cold smoking adds no kiln colour and, in their words, is done
    // that way precisely to keep the enzyme intact.
    id: 'blacklands-mesquite-smoked',
    name: 'Black Coal Mesquite Smoked Malt',
    maker: 'Blacklands Malt',
    type: 'kilned-toasted',
    maltClass: 'base-pale',
    extractFineDryPercent: 80,
    moisturePercent: 4.5,
    colorLovibond: 3,
    diastaticPowerLintner: 90,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'Base malt cold-smoked over mesquite, which Blacklands describe as tasting of Texas brisket — pungent and meaty where Bamberg beech is sweet. The smoke goes on without a kiln, so the enzyme survives and this can be the whole grist of a smoked lager or porter; a tenth of it is already assertive.',
    tags: ['smoked', 'mesquite', 'base', 'rauchbier', 'craft-malt', 'texas'],
  }),

  // ---------------------------------------------------------------------------
  // Colorado Malting Company — Alamosa, Colorado
  // ---------------------------------------------------------------------------
  craftMalt({
    id: 'colorado-malting-pale',
    name: 'SoCO Gold Base Malt',
    maker: 'Colorado Malting Company',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 79,
    moisturePercent: 4.5,
    colorLovibond: 2.4,
    diastaticPowerLintner: 85,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'The house pale, listed in their catalog at 2–3 °L. San Luis Valley farm-malted 2-row: high-desert barley with a clean, lightly sweet flavor and enough enzyme to convert a whole ale or lager grist.',
    tags: ['base', 'two-row', 'craft-malt', 'colorado', 'estate', 'local'],
  }),
  craftMalt({
    id: 'colorado-malting-pilsner',
    name: 'SoCO Light Base Malt',
    maker: 'Colorado Malting Company',
    type: 'base',
    maltClass: 'base-pilsner',
    extractFineDryPercent: 79,
    moisturePercent: 4.5,
    colorLovibond: 1.8,
    diastaticPowerLintner: 95,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'Their pilsner-kilned base, cataloged at 1.7–1.9 °L. Pale, clean and self-converting — the high-altitude lager base from a farm that malts its own barley.',
    tags: ['base', 'pilsner', 'craft-malt', 'colorado', 'estate'],
  }),
  craftMalt({
    id: 'colorado-malting-crystal-60',
    name: 'Crystal 60',
    maker: 'Colorado Malting Company',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 75,
    moisturePercent: 5,
    colorLovibond: 60,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 15,
    needsMashing: false,
    description:
      'Farm-malted mid-color crystal: caramel sweetness and a copper cast. Five to ten percent in amber and brown ales from Alamosa barley.',
    tags: ['crystal', 'caramel', 'craft-malt', 'colorado', 'estate'],
  }),
  craftMalt({
    id: 'colorado-malting-chocolate',
    name: 'Chocolate Malt',
    maker: 'Colorado Malting Company',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 72,
    moisturePercent: 4,
    colorLovibond: 350,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: false,
    description:
      'San Luis Valley barley roasted to chocolate color. Cocoa and coffee without a burnt edge; two to eight percent in porter, stout and brown ale.',
    tags: ['roasted', 'chocolate', 'craft-malt', 'colorado', 'estate'],
  }),
  craftMalt({
    id: 'colorado-malting-wheat',
    name: 'Malted White Wheat',
    maker: 'Colorado Malting Company',
    type: 'base',
    maltClass: 'wheat',
    extractFineDryPercent: 81,
    moisturePercent: 5,
    colorLovibond: 2.4,
    diastaticPowerLintner: 70,
    suggestedMaxPercent: 60,
    needsMashing: true,
    description:
      'Valley-grown wheat malted on the farm. Soft body and lasting foam for wheat beers and hazy ales; rice hulls at high rates.',
    tags: ['base', 'wheat', 'craft-malt', 'colorado', 'estate', 'head-retention'],
  }),
  craftMalt({
    id: 'colorado-malting-peated',
    name: 'Colorado Peated Malt',
    maker: 'Colorado Malting Company',
    type: 'kilned-toasted',
    maltClass: 'base-pale',
    extractFineDryPercent: 78,
    moisturePercent: 4.5,
    colorLovibond: 2.5,
    diastaticPowerLintner: 55,
    suggestedMaxPercent: 60,
    needsMashing: true,
    description:
      'Peat-smoked malt from the San Luis Valley, and the only American-made peated malt most brewers will find. Earthy and medicinal in the Scottish manner; a few percent is plenty unless the smoke is the point.',
    tags: ['peated', 'scotch-ale', 'smoked', 'craft-malt', 'colorado', 'san-luis-valley', 'base'],
  }),
  // Was filed as "Mesquite Smoke". Colorado Malting's smoke line has no mesquite:
  // it is peated, bourbon barrel, apple, beechwood, cherry, oak and cottonwood.
  // The cottonwood one is Alamosa Smoked Malt (catalogue ID CO-ALASMK, 4-5 L),
  // named for the cottonwoods on the upper Rio Grande that gave the town its name.
  //
  // Where did a mesquite malt come from? Almost certainly **Blacklands**, further
  // down this same file, whose Black Coal Mesquite Smoked Malt is real and is a
  // Texas product. That makes this the fifth cross-maker migration found this
  // session, and the first one where the donor is identifiable. Worth remembering
  // that these defects cluster inside a file rather than across the catalogue.
  craftMalt({
    id: 'colorado-malting-mesquite-smoked',
    name: 'Alamosa Smoked Malt',
    maker: 'Colorado Malting Company',
    type: 'kilned-toasted',
    maltClass: 'base-pale',
    extractFineDryPercent: 78,
    moisturePercent: 4.5,
    colorLovibond: 4,
    diastaticPowerLintner: 55,
    suggestedMaxPercent: 60,
    needsMashing: true,
    description:
      'Smoked over cottonwood, the tree that grows along the upper Rio Grande and gave Alamosa its name. Nothing like the beech a German smoked malt is built on: drier and greener, and their own rather than borrowed. Ten to twenty percent carries a beer.',
    tags: ['cottonwood', 'smoked', 'craft-malt', 'colorado', 'san-luis-valley', 'base'],
  }),
  craftMalt({
    id: 'colorado-malting-cherrywood-smoked',
    name: 'Cherry Smoked Malt',
    maker: 'Colorado Malting Company',
    type: 'kilned-toasted',
    maltClass: 'base-pale',
    extractFineDryPercent: 78,
    moisturePercent: 4.5,
    colorLovibond: 4,
    diastaticPowerLintner: 55,
    suggestedMaxPercent: 60,
    needsMashing: true,
    description:
      'Cherry wood: sweet and fruity, the gentlest of the house smokes. Rounds a porter or an amber without announcing itself.',
    tags: ['cherrywood', 'smoked', 'craft-malt', 'colorado', 'san-luis-valley', 'base'],
  }),
  craftMalt({
    id: 'colorado-malting-applewood-smoked',
    name: 'Apple Smoked Malt',
    maker: 'Colorado Malting Company',
    type: 'kilned-toasted',
    maltClass: 'base-pale',
    extractFineDryPercent: 78,
    moisturePercent: 4.5,
    colorLovibond: 4,
    diastaticPowerLintner: 55,
    suggestedMaxPercent: 60,
    needsMashing: true,
    description:
      'Apple wood, sweeter and lighter still than cherry. Seasons a beer rather than defining it, so it suits a smoked lager where beech would be too austere.',
    tags: ['applewood', 'smoked', 'craft-malt', 'colorado', 'san-luis-valley', 'base'],
  }),

  // ---------------------------------------------------------------------------
  // Grouse Malt House — Wellington, Colorado
  // ---------------------------------------------------------------------------
  craftMalt({
    id: 'grouse-pale-millet',
    name: 'Pale Millet Malt',
    maker: 'Grouse Malt House',
    type: 'base',
    maltClass: 'wheat',
    extractFineDryPercent: 74,
    moisturePercent: 6,
    colorLovibond: 1.8,
    diastaticPowerLintner: 50,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'Certified gluten-free pale millet, the house base for gluten-free beer. Sweet, grainy and honeyed, with enough enzyme to convert itself if the rest of the grist is millet too.',
    tags: ['base', 'millet', 'gluten-free', 'craft-malt', 'colorado'],
  }),
  craftMalt({
    id: 'grouse-goldfinch-millet',
    name: 'Goldfinch Millet Malt',
    maker: 'Grouse Malt House',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 73,
    moisturePercent: 5,
    colorLovibond: 8,
    diastaticPowerLintner: 25,
    suggestedMaxPercent: 40,
    needsMashing: true,
    description:
      'Award-winning amber millet with maple, bran, toasted cereal, honey and caramel. A gluten-free Munich stand-in; 10–30 % adds color and sweetness without wheat or barley.',
    tags: ['kilned', 'millet', 'gluten-free', 'craft-malt', 'colorado'],
  }),
  craftMalt({
    id: 'grouse-munich-millet',
    name: 'Munich Millet Malt',
    maker: 'Grouse Malt House',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 73,
    moisturePercent: 5,
    colorLovibond: 2,
    diastaticPowerLintner: 30,
    suggestedMaxPercent: 80,
    needsMashing: true,
    description:
      'Lightly toasted millet with a sweet bready nose, biscuit and light toffee. Pale enough to be a large fraction of a gluten-free amber or Märzen-style beer.',
    tags: ['kilned', 'munich', 'millet', 'gluten-free', 'craft-malt', 'colorado'],
  }),
  craftMalt({
    id: 'grouse-caramel-millet',
    name: 'Caramel Millet Malt',
    maker: 'Grouse Malt House',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 72,
    moisturePercent: 6,
    colorLovibond: 5,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 20,
    needsMashing: false,
    description:
      'Light gluten-free crystal millet: sweet biscuit and caramel with extra residual sugar. Five to fifteen percent rounds a pale millet beer without much color.',
    tags: ['crystal', 'caramel', 'millet', 'gluten-free', 'craft-malt', 'colorado'],
  }),
  craftMalt({
    id: 'grouse-griffin-millet',
    name: 'Griffin Millet Malt',
    maker: 'Grouse Malt House',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 71,
    moisturePercent: 5,
    colorLovibond: 80,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 15,
    needsMashing: false,
    description:
      'Dark millet crystal with plum, toffee, caramelized sugar and chocolate. The gluten-free stand-in for a 80 L crystal; a little goes a long way.',
    tags: ['crystal', 'caramel', 'millet', 'gluten-free', 'craft-malt', 'colorado'],
  }),
  craftMalt({
    id: 'grouse-chocolate-millet',
    name: 'Chocolate Roast Millet',
    maker: 'Grouse Malt House',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 68,
    moisturePercent: 4,
    colorLovibond: 140,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: false,
    description:
      'Millet roasted to cocoa-nibs and coffee. Smooth gluten-free roast for porter and stout that cannot use barley chocolate malt.',
    tags: ['roasted', 'chocolate', 'millet', 'gluten-free', 'craft-malt', 'colorado'],
  }),
  craftMalt({
    id: 'grouse-pale-buckwheat',
    name: 'Pale Buckwheat Malt',
    maker: 'Grouse Malt House',
    type: 'base',
    maltClass: 'wheat',
    extractFineDryPercent: 72,
    moisturePercent: 6,
    colorLovibond: 2.2,
    diastaticPowerLintner: 35,
    suggestedMaxPercent: 40,
    needsMashing: true,
    description:
      'Creamy, nutty gluten-free buckwheat malt. Adds body and foam where millet runs thin; sticky mash, so rice hulls are part of the recipe at high rates.',
    tags: ['base', 'buckwheat', 'gluten-free', 'craft-malt', 'colorado', 'body', 'head-retention'],
  }),
  craftMalt({
    id: 'grouse-pale-rice',
    name: 'Pale Rice Malt',
    maker: 'Grouse Malt House',
    type: 'adjunct-grain',
    maltClass: 'non-buffering',
    extractFineDryPercent: 80,
    moisturePercent: 6,
    colorLovibond: 1.5,
    diastaticPowerLintner: 30,
    suggestedMaxPercent: 40,
    needsMashing: true,
    description:
      'Certified gluten-free pale rice malt: grainy, bready, low grass. Dries and lightens a millet beer the way flaked rice dries a barley lager, with more flavor than flakes.',
    tags: ['adjunct', 'rice', 'gluten-free', 'craft-malt', 'colorado', 'light-body'],
  }),
  craftMalt({
    id: 'grouse-pale-oat',
    name: 'Pale Oat Malt',
    maker: 'Grouse Malt House',
    type: 'adjunct-grain',
    maltClass: 'wheat',
    extractFineDryPercent: 76,
    moisturePercent: 6,
    colorLovibond: 2.5,
    diastaticPowerLintner: 20,
    suggestedMaxPercent: 25,
    needsMashing: true,
    description:
      'Gluten-free certified oat malt (purity-protocol oats). Grainy, nutty cream for gluten-free hazy ales and stouts; less gummy than flakes but still a lauter risk at high rates.',
    tags: ['adjunct', 'oats', 'gluten-free', 'craft-malt', 'colorado', 'mouthfeel'],
  }),
  craftMalt({
    id: 'grouse-malted-yellow-corn',
    name: 'Malted Yellow Corn',
    maker: 'Grouse Malt House',
    type: 'adjunct-grain',
    maltClass: 'non-buffering',
    extractFineDryPercent: 74,
    moisturePercent: 6,
    colorLovibond: 2,
    diastaticPowerLintner: 20,
    suggestedMaxPercent: 40,
    needsMashing: true,
    description:
      'Gluten-free malted maize: sweet and creamy. A stand-in for flaked corn in gluten-free lager and cream ale without a cereal mash.',
    tags: ['adjunct', 'corn', 'maize', 'gluten-free', 'craft-malt', 'colorado'],
  }),
  craftMalt({
    id: 'grouse-vienna-millet',
    name: 'Vienna Millet Malt',
    maker: 'Grouse Malt House',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 74,
    moisturePercent: 8,
    colorLovibond: 3,
    diastaticPowerLintner: 40,
    suggestedMaxPercent: 80,
    needsMashing: true,
    description:
      'Fruity, sweet and bready — a step past the Munich millet without losing the enzyme to carry a grist.',
    tags: [
      'millet',
      'base',
      'vienna',
      'kilned',
      'gluten-free',
      'craft-malt',
      'colorado',
      'organic',
    ],
  }),
  craftMalt({
    id: 'grouse-dark-munich-millet',
    name: 'Dark Munich Millet Malt',
    maker: 'Grouse Malt House',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 72,
    moisturePercent: 8,
    colorLovibond: 9,
    diastaticPowerLintner: 20,
    suggestedMaxPercent: 50,
    needsMashing: true,
    description:
      'Strong biscuit and toast over graham cracker, nutty with a toffee edge. The darkest millet that still carries enzyme.',
    tags: [
      'millet',
      'munich',
      'dark',
      'kilned',
      'gluten-free',
      'craft-malt',
      'colorado',
      'organic',
    ],
  }),
  craftMalt({
    id: 'grouse-cara-millet',
    name: 'Cara Millet Malt',
    maker: 'Grouse Malt House',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 73,
    moisturePercent: 8,
    colorLovibond: 2.3,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 25,
    needsMashing: false,
    description:
      'Sweet caramel and a lift in residual sugar, at almost no color. Body and sweetness for a pale gluten-free beer.',
    tags: ['millet', 'cara', 'crystal', 'body', 'gluten-free', 'craft-malt', 'colorado', 'organic'],
  }),
  craftMalt({
    id: 'grouse-red-wing-millet',
    name: 'Red Wing Millet Malt',
    maker: 'Grouse Malt House',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 71,
    moisturePercent: 7,
    colorLovibond: 35,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 20,
    needsMashing: false,
    description:
      'Caramel, toffee, raisin and plum over sweet breakfast cereal. The mid-range crystal millet, and the one that gives a gluten-free amber its color.',
    tags: [
      'millet',
      'crystal',
      'caramel',
      'raisin',
      'amber',
      'gluten-free',
      'craft-malt',
      'colorado',
      'organic',
    ],
  }),
  craftMalt({
    id: 'grouse-light-roast-millet',
    name: 'Light Roast Millet Malt',
    maker: 'Grouse Malt House',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 72,
    moisturePercent: 5,
    colorLovibond: 18,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 20,
    needsMashing: false,
    description:
      'Softly toasted with a slight biscuit note and a nuttiness behind it. The gentlest of the house roasts.',
    tags: [
      'millet',
      'roasted',
      'light-roast',
      'biscuit',
      'gluten-free',
      'craft-malt',
      'colorado',
      'organic',
    ],
  }),
  craftMalt({
    id: 'grouse-dutch-roast-millet',
    name: 'Dutch Roast Millet Malt',
    maker: 'Grouse Malt House',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 71,
    moisturePercent: 5,
    colorLovibond: 25,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 20,
    needsMashing: false,
    description:
      'Lightly toasted, with almond and cashew rather than cocoa. An unusual roast and a useful one in a gluten-free brown.',
    tags: [
      'millet',
      'roasted',
      'almond',
      'nutty',
      'gluten-free',
      'craft-malt',
      'colorado',
      'organic',
    ],
  }),
  craftMalt({
    id: 'grouse-medium-roast-millet',
    name: 'Medium Roast Millet Malt',
    maker: 'Grouse Malt House',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 70,
    moisturePercent: 5,
    colorLovibond: 48,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 15,
    needsMashing: false,
    description:
      'Caramel and biscuit with the first suggestion of coffee. The middle of the roast ladder.',
    tags: [
      'millet',
      'roasted',
      'caramel',
      'coffee',
      'gluten-free',
      'craft-malt',
      'colorado',
      'organic',
    ],
  }),
  craftMalt({
    id: 'grouse-roasted-goldfinch-millet',
    name: 'Roasted Goldfinch Millet Malt',
    maker: 'Grouse Malt House',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 70,
    moisturePercent: 5,
    colorLovibond: 95,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 12,
    needsMashing: false,
    description:
      'Their Goldfinch taken to a roast: brown sugar and caramel deepened rather than replaced by the kiln.',
    tags: [
      'millet',
      'roasted',
      'goldfinch',
      'brown-sugar',
      'gluten-free',
      'craft-malt',
      'colorado',
      'organic',
    ],
  }),
  craftMalt({
    id: 'grouse-roasted-cara-millet',
    name: 'Roasted Cara Millet Malt',
    maker: 'Grouse Malt House',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 71,
    moisturePercent: 5,
    colorLovibond: 30,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 15,
    needsMashing: false,
    description:
      'Rich toffee and honey with a sweetness that lingers past the roast. Between a crystal and a roast, which is the point of it.',
    tags: [
      'millet',
      'roasted',
      'cara',
      'toffee',
      'honey',
      'gluten-free',
      'craft-malt',
      'colorado',
      'organic',
    ],
  }),
  craftMalt({
    id: 'grouse-caramel-90-millet',
    name: 'Caramel 90L Millet Malt',
    maker: 'Grouse Malt House',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 70,
    moisturePercent: 7,
    colorLovibond: 123,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 12,
    needsMashing: false,
    description:
      'Honey, caramel and brown sugar over sweet bread. The dark half of the caramel millet range.',
    tags: [
      'millet',
      'crystal',
      'caramel',
      'dark',
      'honey',
      'gluten-free',
      'craft-malt',
      'colorado',
      'organic',
    ],
  }),
  craftMalt({
    id: 'grouse-caramel-120-millet',
    name: 'Caramel 120L Millet Malt',
    maker: 'Grouse Malt House',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 69,
    moisturePercent: 7,
    colorLovibond: 165,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: false,
    description: 'Toffee and caramel with light chocolate and maple sugar behind them.',
    tags: [
      'millet',
      'crystal',
      'caramel',
      'dark',
      'maple',
      'gluten-free',
      'craft-malt',
      'colorado',
      'organic',
    ],
  }),
  craftMalt({
    id: 'grouse-caramel-240-millet',
    name: 'Caramel 240L Millet Malt',
    maker: 'Grouse Malt House',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 68,
    moisturePercent: 7,
    colorLovibond: 325,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 8,
    needsMashing: false,
    description:
      'Sweet chocolate, dark toffee and molasses, and notably low in coffee character for the color. A way to darken a gluten-free stout without the roast bite.',
    tags: [
      'millet',
      'crystal',
      'caramel',
      'chocolate',
      'molasses',
      'stout',
      'gluten-free',
      'craft-malt',
      'colorado',
      'organic',
    ],
  }),
  craftMalt({
    id: 'grouse-roasted-buckwheat',
    name: 'Roasted Buckwheat Malt',
    maker: 'Grouse Malt House',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 68,
    moisturePercent: 7,
    colorLovibond: 40,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 15,
    needsMashing: false,
    description:
      'Nutty with a slight chocolate note, and smoother than a roasted millet of the same color.',
    tags: [
      'buckwheat',
      'roasted',
      'nutty',
      'smooth',
      'gluten-free',
      'craft-malt',
      'colorado',
      'organic',
    ],
  }),
  craftMalt({
    id: 'grouse-caramel-buckwheat',
    name: 'Caramel Buckwheat Malt',
    maker: 'Grouse Malt House',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 70,
    moisturePercent: 7,
    colorLovibond: 4.5,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 20,
    needsMashing: false,
    description:
      "Sweet bread, caramel and buckwheat's own nuttiness, improving body and foam at almost no color.",
    tags: [
      'buckwheat',
      'crystal',
      'caramel',
      'body',
      'foam',
      'gluten-free',
      'craft-malt',
      'colorado',
      'organic',
    ],
  }),
  craftMalt({
    id: 'grouse-rolled-oats',
    name: 'Rolled Oats',
    maker: 'Grouse Malt House',
    type: 'adjunct-grain',
    maltClass: 'non-buffering',
    extractFineDryPercent: 75,
    moisturePercent: 9,
    colorLovibond: 2,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 30,
    needsMashing: true,
    description:
      'Certified gluten-free rolled oats: soft cereal and oat, added for body rather than for flavor.',
    tags: [
      'oats',
      'flaked',
      'adjunct',
      'body',
      'mouthfeel',
      'gluten-free',
      'craft-malt',
      'colorado',
      'organic',
    ],
  }),
  craftMalt({
    id: 'grouse-flaked-quinoa',
    name: 'Flaked Quinoa',
    maker: 'Grouse Malt House',
    type: 'adjunct-grain',
    maltClass: 'non-buffering',
    extractFineDryPercent: 70,
    moisturePercent: 9,
    colorLovibond: 2,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 15,
    needsMashing: true,
    description:
      'Earthy with a green-pepper note, and a body and foam adjunct rather than a flavor one. A little goes a long way.',
    tags: [
      'quinoa',
      'flaked',
      'adjunct',
      'body',
      'foam',
      'gluten-free',
      'craft-malt',
      'colorado',
      'organic',
    ],
  }),

  // ---------------------------------------------------------------------------
  // Root Shoot Malting — Loveland, Colorado
  // ---------------------------------------------------------------------------
  craftMalt({
    id: 'root-shoot-low-color-pilsner',
    name: 'Low Color Pilsner',
    maker: 'Root Shoot Malting',
    type: 'base',
    maltClass: 'base-pilsner',
    extractFineDryPercent: 81,
    moisturePercent: 4.5,
    colorLovibond: 1.5,
    diastaticPowerLintner: 100,
    suggestedMaxPercent: 100,
    needsMashing: true,
    provenance: 'spec-sheet',
    description:
      'Ultra-pale estate 2-row from Thunder and Genie barley. Delicate grain and subtle sweetness at 1.3–1.8 SRM — the showcase lager and super-pale IPA base from a fifth-generation Loveland farm.',
    tags: ['base', 'pilsner', 'craft-malt', 'colorado', 'estate', 'pale'],
  }),
  craftMalt({
    id: 'root-shoot-genie-pale',
    name: 'Genie Pale',
    maker: 'Root Shoot Malting',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 80,
    moisturePercent: 4.5,
    colorLovibond: 2.4,
    diastaticPowerLintner: 90,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'Single-variety pale from estate Genie spring 2-row. Bread and honey, more flavorful than commodity two-row, and it will carry any ale or lager on its own.',
    tags: ['base', 'pale', 'craft-malt', 'colorado', 'estate', 'genie'],
  }),
  craftMalt({
    id: 'root-shoot-genie-pilsner',
    name: 'Genie Pilsner',
    maker: 'Root Shoot Malting',
    type: 'base',
    maltClass: 'base-pilsner',
    extractFineDryPercent: 80,
    moisturePercent: 4.5,
    colorLovibond: 1.7,
    diastaticPowerLintner: 95,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'Genie 2-row kilned as a traditional pilsner: straw-forward, bready undertones, excellent clarity. The German-lager and pale-IPA canvas from the same variety as Genie Pale.',
    tags: ['base', 'pilsner', 'craft-malt', 'colorado', 'estate', 'genie'],
  }),
  craftMalt({
    id: 'root-shoot-english-pale-ale',
    name: 'English Pale Ale',
    maker: 'Root Shoot Malting',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 79,
    moisturePercent: 4.5,
    colorLovibond: 3.4,
    diastaticPowerLintner: 70,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'Estate take on Maris Otter or Golden Promise: biscuit, nut and saltine cracker with a dry finish. Built for bitter, pale ale and IPA that want a warmer malt floor.',
    tags: ['base', 'pale-ale', 'craft-malt', 'colorado', 'estate', 'english-style'],
  }),
  craftMalt({
    id: 'root-shoot-light-munich-4',
    name: 'Light Munich 4',
    maker: 'Root Shoot Malting',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 79,
    moisturePercent: 4.5,
    colorLovibond: 4,
    diastaticPowerLintner: 70,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'Lightly toasted estate Munich, still a base malt. Mild sweetness and gold color with strong enzymatic power — also used by distillers who want bourbon depth from malted barley.',
    tags: ['kilned', 'munich', 'craft-malt', 'colorado', 'estate', 'base'],
  }),
  craftMalt({
    id: 'root-shoot-vienna',
    name: 'Vienna Malt',
    maker: 'Root Shoot Malting',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 78,
    moisturePercent: 4.5,
    colorLovibond: 5,
    diastaticPowerLintner: 55,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'Kilned a shade darker than textbook Vienna: graham cracker, Grape-Nuts and a honeysuckle finish. Self-converting as a whole grist or a complementary flavor.',
    tags: ['kilned', 'vienna', 'craft-malt', 'colorado', 'estate'],
  }),
  craftMalt({
    id: 'root-shoot-white-wheat',
    name: 'Malted White Wheat',
    maker: 'Root Shoot Malting',
    type: 'base',
    maltClass: 'wheat',
    extractFineDryPercent: 82,
    moisturePercent: 5,
    colorLovibond: 2.4,
    diastaticPowerLintner: 70,
    suggestedMaxPercent: 60,
    needsMashing: true,
    description:
      'Huffman winter wheat grown on the farm. Sweet, bready and doughy — foam and mouthfeel for wheat beers and hazy American ales.',
    tags: ['base', 'wheat', 'craft-malt', 'colorado', 'estate', 'head-retention'],
  }),
  craftMalt({
    id: 'root-shoot-rye',
    name: 'Malted Rye',
    maker: 'Root Shoot Malting',
    type: 'base',
    maltClass: 'wheat',
    extractFineDryPercent: 80,
    moisturePercent: 5,
    colorLovibond: 3.5,
    diastaticPowerLintner: 50,
    suggestedMaxPercent: 50,
    needsMashing: true,
    description:
      'Guardian winter rye from the Loveland farm. Earthy sunflower-seed spice and bread crust for rye ale and rye whiskey; hulls at high rates.',
    tags: ['base', 'rye', 'craft-malt', 'colorado', 'estate', 'spicy'],
  }),
  craftMalt({
    id: 'root-shoot-cara-ruby',
    name: 'Cara Ruby',
    maker: 'Root Shoot Malting',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 74,
    moisturePercent: 5.5,
    colorLovibond: 30,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 15,
    needsMashing: false,
    description:
      'Genie 2-row crystal that throws a ruby tint, with floral aroma and walnut-like light caramel. Unfermentable sugars add body without a heavy crystal sweetness.',
    tags: ['crystal', 'caramel', 'craft-malt', 'colorado', 'estate', 'red'],
  }),
  craftMalt({
    id: 'root-shoot-buck-naked-chocolate',
    name: 'Buck Naked Chocolate',
    maker: 'Root Shoot Malting',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 70,
    moisturePercent: 4,
    colorLovibond: 350,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: false,
    description:
      'Hulless barley grown and malted by Root Shoot, roasted by Troubadour Maltings. Bold cocoa and coffee without husky astringency — a debittered chocolate from two Colorado houses.',
    tags: ['roasted', 'chocolate', 'hulless', 'craft-malt', 'colorado', 'estate'],
  }),
  craftMalt({
    id: 'root-shoot-buck-naked-pilsner',
    name: "Buck 'Naked' Pilsner",
    maker: 'Root Shoot Malting',
    type: 'base',
    maltClass: 'base-pilsner',
    extractFineDryPercent: 80,
    moisturePercent: 4.5,
    colorLovibond: 1.8,
    diastaticPowerLintner: 90,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'Colorado-grown naked barley, which threshes free of its husk. Clean and powerful, and markedly lower in tannin than a hulled pilsner malt because there is no husk to extract from — though that also means it lauters poorly without rice hulls.',
    tags: ['base', 'pilsner', 'naked-barley', 'huskless', 'low-tannin', 'craft-malt', 'colorado'],
  }),
  craftMalt({
    id: 'root-shoot-distillers-malt',
    name: 'Distillers Malt',
    maker: 'Root Shoot Malting',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 80,
    moisturePercent: 4.5,
    colorLovibond: 1.8,
    diastaticPowerLintner: 140,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'Odyssey two-row kilned cool to protect the enzyme, so the diastatic power is high enough to convert a large adjunct charge. Built for the still rather than the kettle, and useful in beer wherever a grist is short of enzyme.',
    tags: ['base', 'distilling', 'high-dp', 'enzyme', 'craft-malt', 'colorado'],
  }),
  craftMalt({
    id: 'root-shoot-chit-malt',
    name: 'Chit Malt',
    maker: 'Root Shoot Malting',
    type: 'base',
    maltClass: 'base-pilsner',
    extractFineDryPercent: 72,
    moisturePercent: 6,
    colorLovibond: 1.5,
    diastaticPowerLintner: 55,
    suggestedMaxPercent: 20,
    needsMashing: true,
    description:
      'Barely malted barley — green, grainy and raw grass. Almost no flavor of its own; it is added for body, head retention and haze stability.',
    tags: ['chit', 'head-retention', 'body', 'haze', 'craft-malt', 'colorado'],
  }),
  craftMalt({
    id: 'root-shoot-honey-malt',
    name: 'Honey Malt',
    maker: 'Root Shoot Malting',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 75,
    moisturePercent: 5.5,
    colorLovibond: 25,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 20,
    needsMashing: false,
    description:
      'Grapefruit-tart sweetness finishing on bread crust, with a pretzel aroma and a raisin note behind it. Stewed rather than kilned, so it carries a lower pH than its color suggests.',
    tags: ['crystal', 'honey', 'sweet', 'stewed', 'craft-malt', 'colorado'],
  }),
  craftMalt({
    id: 'root-shoot-malted-oats',
    name: 'Malted Oats',
    maker: 'Root Shoot Malting',
    type: 'base',
    maltClass: 'wheat',
    extractFineDryPercent: 72,
    moisturePercent: 6,
    colorLovibond: 3,
    diastaticPowerLintner: 25,
    suggestedMaxPercent: 30,
    needsMashing: true,
    description:
      'Monida spring oats, malted rather than flaked: significant body and mouthfeel with a slight grainy character. What a stout or a hazy IPA is built on.',
    tags: ['oat', 'malted-oats', 'mouthfeel', 'hazy', 'stout', 'craft-malt', 'colorado'],
  }),
  craftMalt({
    id: 'root-shoot-malted-oland-wheat',
    name: 'Malted Oland Wheat',
    maker: 'Root Shoot Malting',
    type: 'base',
    maltClass: 'wheat',
    extractFineDryPercent: 77,
    moisturePercent: 5.5,
    colorLovibond: 3,
    diastaticPowerLintner: 60,
    suggestedMaxPercent: 50,
    needsMashing: true,
    description:
      'A heritage soft wheat, open-source and non-GMO, higher in protein than a modern wheat. Full-bodied and built for farmhouse ales and hefeweizen.',
    tags: ['wheat', 'heritage', 'oland', 'farmhouse', 'hefeweizen', 'craft-malt', 'colorado'],
  }),
  craftMalt({
    id: 'root-shoot-malted-yellow-corn',
    name: 'Malted Yellow Corn',
    maker: 'Root Shoot Malting',
    type: 'adjunct-grain',
    maltClass: 'non-buffering',
    extractFineDryPercent: 79,
    moisturePercent: 10,
    colorLovibond: 2,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 30,
    needsMashing: true,
    description:
      'Non-GMO corn, malted rather than flaked: sweet, smooth and pale straw. For North American lagers, and for bourbon mash bills that want the corn to taste of something.',
    tags: ['corn', 'maize', 'adjunct', 'lager', 'gluten-free', 'craft-malt', 'colorado'],
  }),
  craftMalt({
    id: 'root-shoot-munich-10',
    name: 'Munich 10',
    maker: 'Root Shoot Malting',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 77,
    moisturePercent: 4.5,
    colorLovibond: 10,
    diastaticPowerLintner: 25,
    suggestedMaxPercent: 50,
    needsMashing: true,
    description:
      'The darker of the two house Munichs: moderately sweet, with caramel, honey and brown sugar over cracker and bread crust, and an orange hue. Still carries enough enzyme to be most of a grist.',
    tags: ['kilned', 'munich', 'malty', 'amber', 'craft-malt', 'colorado'],
  }),

  // ---------------------------------------------------------------------------
  // Sinagua Malt — Camp Verde, Arizona
  // ---------------------------------------------------------------------------
  craftMalt({
    id: 'sinagua-base',
    name: 'Verde Valley Pale Base Malt',
    maker: 'Sinagua Malt',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 78,
    moisturePercent: 4.5,
    colorLovibond: 2.2,
    diastaticPowerLintner: 80,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'The house base malt, from winter barley grown within a few miles of the malthouse so farmers can leave thirsty summer crops out of the ground and keep water in the Verde River. Clean and lightly sweet, and it will carry a whole Arizona grist on its own.',
    tags: ['base', 'ale', 'craft-malt', 'arizona', 'local', 'low-water'],
  }),
  craftMalt({
    id: 'sinagua-sonoran-white-wheat',
    name: 'Malted Sonoran White Wheat',
    maker: 'Sinagua Malt',
    type: 'base',
    maltClass: 'wheat',
    extractFineDryPercent: 80,
    moisturePercent: 5,
    colorLovibond: 2.4,
    diastaticPowerLintner: 65,
    suggestedMaxPercent: 60,
    needsMashing: true,
    description:
      'Sonoran white wheat, the soft heritage wheat the Southwest has grown since the missions, malted in Camp Verde. Soft body and lasting foam for a wit or a hazy ale, with a gentler, more floury character than a modern hard wheat.',
    tags: ['base', 'wheat', 'heritage', 'sonoran', 'craft-malt', 'arizona', 'head-retention'],
  }),
  craftMalt({
    id: 'sinagua-biscuit',
    name: 'Biscuit Malt',
    maker: 'Sinagua Malt',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 74,
    moisturePercent: 3.5,
    colorLovibond: 25,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 15,
    needsMashing: false,
    description:
      'The gentlest rung of their roast ladder: toasted bread crust and a dry biscuit nuttiness, well short of cocoa. Five to ten percent gives an amber or a brown ale a baked depth without much color.',
    tags: ['roasted', 'biscuit', 'toasted', 'craft-malt', 'arizona', 'local'],
  }),
  craftMalt({
    id: 'sinagua-pale-chocolate',
    name: 'Pale Chocolate Malt',
    maker: 'Sinagua Malt',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 72,
    moisturePercent: 4,
    colorLovibond: 200,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: false,
    description:
      'Taken off the roaster short of full chocolate malt: cocoa and light coffee with a softer edge and much less astringency. Two to six percent colors a porter or a dark mild and keeps the roast smooth.',
    tags: ['roasted', 'chocolate', 'pale-chocolate', 'porter', 'craft-malt', 'arizona'],
  }),

  // ---------------------------------------------------------------------------
  // TexMalt — Fort Worth, Texas
  // ---------------------------------------------------------------------------
  craftMalt({
    id: 'texmalt-pale',
    name: 'San Jacinto Heritage Malt',
    maker: 'TexMalt',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 79,
    moisturePercent: 4.5,
    colorLovibond: 2.1,
    diastaticPowerLintner: 75,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'The everyday Fort Worth two-row, fully modified at 1.9–2.2 °L: bready with a hint of honey and a light floral sweetness. Fresher and rounder than commodity pale malt, and it will carry an ale grist on its own.',
    tags: ['base', 'ale', 'two-row', 'craft-malt', 'texas', 'local'],
    url: 'https://www.texmalt.com/malts/1',
  }),
  craftMalt({
    id: 'texmalt-llano-pilsner',
    name: 'Llano Pilsner Malt',
    maker: 'TexMalt',
    type: 'base',
    maltClass: 'base-pilsner',
    extractFineDryPercent: 80,
    moisturePercent: 4.9,
    colorLovibond: 1.7,
    diastaticPowerLintner: 85,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'Barley off the Llano Estacado, modified cool and slow and then kilned gently to keep the place in the grain: straw, honeysuckle and a light earthiness over a mild sweetness. The Texas lager base, at 1.6–1.8 °L.',
    tags: ['base', 'pilsner', 'lager', 'craft-malt', 'texas', 'local'],
    url: 'https://www.texmalt.com/malts/3',
  }),
  craftMalt({
    id: 'texmalt-wildfire-pale',
    name: 'Wildfire Pale Malt',
    maker: 'TexMalt',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 78,
    moisturePercent: 4.5,
    colorLovibond: 4.1,
    diastaticPowerLintner: 60,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'Kilned a good deal darker than their heritage two-row, at 3.8–4.5 °L: bready biscuit and a full body with a hint of caramel and toffee. Enzyme to spare, and enough color to set the floor under a balanced IPA.',
    tags: ['base', 'pale-ale', 'ipa', 'craft-malt', 'texas', 'local'],
    url: 'https://www.texmalt.com/malts/4',
  }),
  craftMalt({
    id: 'texmalt-distillers',
    name: 'Texas Distillers Malt',
    maker: 'TexMalt',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 77,
    moisturePercent: 5,
    colorLovibond: 2.5,
    diastaticPowerLintner: 150,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      "Six-row Texas barley kilned for enzyme rather than for flavor, at 2.2–2.8 °L and a published diastatic power past 230. It is here to convert somebody else's starch — a corn-heavy bourbon mash, or the flakes in a crisp American lager.",
    tags: ['base', 'distilling', 'six-row', 'high-dp', 'enzyme', 'craft-malt', 'texas'],
    url: 'https://www.texmalt.com/malts/2',
  }),
  craftMalt({
    id: 'texmalt-wheat',
    name: 'Denton County Wheat Malt',
    maker: 'TexMalt',
    type: 'base',
    maltClass: 'wheat',
    extractFineDryPercent: 81,
    moisturePercent: 5,
    colorLovibond: 1.4,
    diastaticPowerLintner: 100,
    suggestedMaxPercent: 60,
    needsMashing: true,
    description:
      'Soft red winter wheat from Denton County, and paler than most wheat malts at 1.3–1.5 °L: smooth and soft with a sweet white-bread finish. Haze, foam and mouthfeel for weissbier, wit and hazy IPA; rice hulls at high rates.',
    tags: ['base', 'wheat', 'craft-malt', 'texas', 'head-retention', 'haze'],
    url: 'https://www.texmalt.com/malts/6',
  }),
  craftMalt({
    id: 'texmalt-rye',
    name: 'Red River Rye Malt',
    maker: 'TexMalt',
    type: 'base',
    maltClass: 'wheat',
    extractFineDryPercent: 80,
    moisturePercent: 5,
    colorLovibond: 5.5,
    diastaticPowerLintner: 50,
    suggestedMaxPercent: 50,
    needsMashing: true,
    description:
      'Texas rye at 5–6 °L: clove and sweet licorice over toasted bread crust, with the herbal spice that gives a rye ale or a rye whiskey its edge. Hulls keep the mash from sealing once it is much past a third of the grist.',
    tags: ['base', 'rye', 'craft-malt', 'texas', 'spicy'],
    url: 'https://www.texmalt.com/malts/5',
  }),
  craftMalt({
    id: 'texmalt-triticale',
    name: 'Texas Triticale Malt',
    maker: 'TexMalt',
    type: 'base',
    maltClass: 'wheat',
    extractFineDryPercent: 82,
    moisturePercent: 5,
    colorLovibond: 5.5,
    diastaticPowerLintner: 110,
    suggestedMaxPercent: 50,
    needsMashing: true,
    description:
      'The wheat-rye hybrid, malted at 5–6 °L: delicate herbal spice over freshly baked bread. It hazes and builds body like wheat and spices like rye, so it is worth reaching for where either alone would be plain.',
    tags: ['base', 'triticale', 'wheat', 'rye', 'craft-malt', 'texas', 'body'],
    url: 'https://www.texmalt.com/malts/8',
  }),
  craftMalt({
    id: 'texmalt-aurum-oat',
    name: 'Aurum Oat Malt',
    maker: 'TexMalt',
    type: 'base',
    maltClass: 'wheat',
    extractFineDryPercent: 79,
    moisturePercent: 5,
    colorLovibond: 3.9,
    diastaticPowerLintner: 25,
    suggestedMaxPercent: 30,
    needsMashing: true,
    description:
      'A traditional naked oat variety, malted rather than flaked, at 3.8–4.0 °L: almond, vanilla and honey with a smooth, rich mouthfeel. It holds a head and adds haze, which is most of why a hazy IPA or an oatmeal stout wants it.',
    tags: ['base', 'oats', 'naked-oat', 'craft-malt', 'texas', 'mouthfeel', 'head-retention'],
    url: 'https://www.texmalt.com/malts/7',
  }),
  craftMalt({
    id: 'texmalt-corn',
    name: 'Texas Red Corn Malt',
    maker: 'TexMalt',
    type: 'adjunct-grain',
    maltClass: 'non-buffering',
    extractFineDryPercent: 75,
    moisturePercent: 5.5,
    colorLovibond: 2.1,
    diastaticPowerLintner: 20,
    suggestedMaxPercent: 40,
    needsMashing: true,
    description:
      'Open-pollinated heirloom red corn, malted rather than flaked: fresh corn and a light sweetness with hints of cherry and fresh tomato behind it. For pre-Prohibition lager and for whiskey mash bills that want the maize to taste of something.',
    tags: ['adjunct', 'corn', 'maize', 'heirloom', 'craft-malt', 'texas', 'pre-prohibition'],
    url: 'https://www.texmalt.com/malts/9',
  }),
  craftMalt({
    id: 'texmalt-blue-corn',
    name: 'Texas Blue Corn Malt',
    maker: 'TexMalt',
    type: 'adjunct-grain',
    maltClass: 'non-buffering',
    extractFineDryPercent: 74,
    moisturePercent: 5.5,
    colorLovibond: 1.9,
    diastaticPowerLintner: 20,
    suggestedMaxPercent: 40,
    needsMashing: true,
    description:
      'Heirloom open-pollinated blue corn at 1.8–2.0 °L: fresh corn sweetness with sweet tortilla dough and homemade grits behind it. An adjunct with a flavor of its own, for cream ale, lager and corn whiskey.',
    tags: ['adjunct', 'corn', 'maize', 'heirloom', 'blue-corn', 'craft-malt', 'texas'],
    url: 'https://www.texmalt.com/malts/10',
  }),
  craftMalt({
    id: 'texmalt-white-corn',
    name: 'Texas White Corn Malt',
    maker: 'TexMalt',
    type: 'adjunct-grain',
    maltClass: 'non-buffering',
    extractFineDryPercent: 75,
    moisturePercent: 5.5,
    colorLovibond: 2,
    diastaticPowerLintner: 20,
    suggestedMaxPercent: 40,
    needsMashing: true,
    description:
      'Fresh-picked corn with a hint of toasted tortilla, and the one TexMalt point at American cream ale. Malting the maize does what flakes cannot: it brings sweetness and a little corn flavor instead of thin, neutral starch.',
    tags: ['adjunct', 'corn', 'maize', 'cream-ale', 'craft-malt', 'texas'],
    url: 'https://www.texmalt.com/malts/11',
  }),

  // ---------------------------------------------------------------------------
  // Troubadour Maltings — Fort Collins, Colorado
  // ---------------------------------------------------------------------------
  craftMalt({
    id: 'troubadour-pevec',
    name: 'Pevec',
    maker: 'Troubadour Maltings',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 79,
    moisturePercent: 4.5,
    colorLovibond: 2.4,
    diastaticPowerLintner: 80,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'Czech pilsner-style malt cured cool, at 2–2.5 °L: light honey and white grape over a dry mouthfeel and a notably pale liquor.',
    tags: ['base', 'pilsner', 'czech', 'ale', 'craft-malt', 'colorado', 'local'],
  }),
  craftMalt({
    id: 'troubadour-blue',
    name: 'Blue',
    maker: 'Troubadour Maltings',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 77,
    moisturePercent: 4.5,
    colorLovibond: 9,
    diastaticPowerLintner: 40,
    suggestedMaxPercent: 60,
    needsMashing: true,
    description:
      'Kilned hotter than Serenade, at 8–12 °L: deep caramel and golden raisin with a hint of toasted grain, landing light amber.',
    tags: ['kilned', 'munich', 'amber', 'caramel', 'craft-malt', 'colorado', 'local'],
  }),
  craftMalt({
    id: 'troubadour-ballad',
    name: 'Ballad',
    maker: 'Troubadour Maltings',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 78,
    moisturePercent: 4.5,
    colorLovibond: 7,
    diastaticPowerLintner: 40,
    suggestedMaxPercent: 60,
    needsMashing: true,
    description:
      'Their Munich-style malt at 6–8 °L: floral honey with almond and ripe apricot, and a weight in the mouth the color does not predict. Deep golden rather than amber.',
    tags: ['kilned', 'munich', 'malty', 'honey', 'craft-malt', 'colorado', 'local'],
  }),
  craftMalt({
    id: 'troubadour-encore',
    name: 'Encore',
    maker: 'Troubadour Maltings',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 75,
    moisturePercent: 5.5,
    colorLovibond: 40,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 15,
    needsMashing: false,
    description:
      'A cara-hybrid, only partly crystallized, at 35–50 °L: toffee, cocoa, red raisin and toasted almond, with a ruby-coffee cast.',
    tags: ['crystal', 'caramel', 'cara', 'toffee', 'craft-malt', 'colorado', 'local'],
  }),
  craftMalt({
    id: 'troubadour-moonlight-serenade',
    name: 'Moonlight Serenade',
    maker: 'Troubadour Maltings',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 72,
    moisturePercent: 4,
    colorLovibond: 350,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: false,
    description:
      'Their Serenade base taken to a roast at 300–350 °L: semi-sweet cocoa, caramelized sugar and a herbaceous floral note, giving a light-bodied liquor for its color.',
    tags: ['roasted', 'chocolate', 'cocoa', 'porter', 'craft-malt', 'colorado', 'local'],
  }),
  craftMalt({
    id: 'troubadour-serenade',
    name: 'Serenade',
    maker: 'Troubadour Maltings',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 78,
    moisturePercent: 4.5,
    colorLovibond: 4.5,
    diastaticPowerLintner: 55,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'The house base malt, sitting between an English pale ale malt and a Vienna at 3–4.5 °L: rich floral honey, almond and lightly toasted grain, golden with an orange cast.',
    tags: ['base', 'vienna', 'pale', 'ale', 'craft-malt', 'colorado', 'local'],
  }),

  // ---------------------------------------------------------------------------
  // Montana Craft Malt — Montana
  //
  // Colour, extract and moisture are the maker's published limits or bands
  // (`montanacraftmalt.com/our-products/`). They publish no diastatic power,
  // so every enzymatic row carries a class-typical °Lintner and the whole
  // set is `representative` (the craftMalt default). Dextrin publishes no
  // extract; that row takes the class-typical dextrin figure. Oat malt
  // publishes a 58 % FGDB minimum — hull-on oats, and below the barley
  // extract floor in `craft-consistency.test.ts`, which names this id.
  //
  // The products page is a family listing, so no row carries a `url`.
  // ---------------------------------------------------------------------------
  craftMalt({
    id: 'mcm-bavarian-pilsner',
    name: 'Bavarian Pilsner',
    maker: 'Montana Craft Malt',
    type: 'base',
    maltClass: 'base-pilsner',
    extractFineDryPercent: 81.5,
    moisturePercent: 5,
    colorLovibond: 2.2,
    diastaticPowerLintner: 85,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'European barley varieties grown in Montana and malted as a German pilsner: straw-pale, grassy and faintly honeyed. A lager canvas with more grain character than their North American pilsner.',
    tags: ['base', 'pilsner', 'lager', 'craft-malt', 'montana', 'local'],
  }),
  craftMalt({
    id: 'mcm-munich-light',
    name: 'Munich Light',
    maker: 'Montana Craft Malt',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 80,
    moisturePercent: 5,
    colorLovibond: 5.4,
    diastaticPowerLintner: 40,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'A light Munich, rich and toasty with honey and bread. Dark-gold rather than amber, and still converting enough to be a large share of a festbier or bock grist.',
    tags: ['kilned', 'munich', 'craft-malt', 'montana', 'local'],
  }),
  craftMalt({
    id: 'mcm-british-pale',
    name: 'British Pale',
    maker: 'Montana Craft Malt',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 80,
    moisturePercent: 5,
    colorLovibond: 2.6,
    diastaticPowerLintner: 70,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'Cured harder than the 2-row so melanoidins show: bread crust, toast and biscuit. An ale base for session beer, IPA and stout that wants more malt than a pale two-row gives.',
    tags: ['base', 'pale-ale', 'british', 'craft-malt', 'montana', 'local'],
  }),
  craftMalt({
    id: 'mcm-vienna',
    name: 'Vienna',
    maker: 'Montana Craft Malt',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 80,
    moisturePercent: 5,
    colorLovibond: 3.3,
    diastaticPowerLintner: 50,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'The British Pale kilned further. Bready, biscuity and still pale enough to be the whole grist for an amber lager or a malt-forward ale.',
    tags: ['kilned', 'vienna', 'craft-malt', 'montana', 'local'],
  }),
  craftMalt({
    id: 'mcm-melanoidin-gold',
    name: 'Melanoidin Gold',
    maker: 'Montana Craft Malt',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 80,
    moisturePercent: 5,
    colorLovibond: 7.5,
    diastaticPowerLintner: 20,
    suggestedMaxPercent: 20,
    needsMashing: true,
    description:
      'A pale melanoidin: honey, a hint of stone fruit and biscuit, standing in for a decoction without much color. Keep it under 10 % in pale beer and 20 % in stout.',
    tags: ['kilned', 'melanoidin', 'decoction', 'craft-malt', 'montana'],
  }),
  craftMalt({
    id: 'mcm-cara-20',
    name: 'Cara 20',
    maker: 'Montana Craft Malt',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 80,
    moisturePercent: 5,
    colorLovibond: 14.6,
    diastaticPowerLintner: 15,
    suggestedMaxPercent: 15,
    needsMashing: true,
    description:
      'Kilned like Melanoidin Gold, then cured hotter: copper wort with graham cracker and caramel. Typically under 5 %, and not more than 15 %.',
    tags: ['kilned', 'caramel', 'graham', 'craft-malt', 'montana'],
  }),
  craftMalt({
    id: 'mcm-dextrin',
    name: 'Dextrin',
    maker: 'Montana Craft Malt',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 78,
    moisturePercent: 7,
    colorLovibond: 2,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: false,
    description:
      'Short-germinated, high-protein dextrin malt for head, haze and body. Grainy rather than sweet; 5 % is the usual dose, 10 % the ceiling.',
    tags: ['dextrin', 'body', 'head-retention', 'haze', 'craft-malt', 'montana'],
  }),
  craftMalt({
    id: 'mcm-pale-white-wheat',
    name: 'Pale White Wheat Malt',
    maker: 'Montana Craft Malt',
    type: 'base',
    maltClass: 'wheat',
    extractFineDryPercent: 83,
    moisturePercent: 6.5,
    colorLovibond: 3,
    diastaticPowerLintner: 55,
    suggestedMaxPercent: 80,
    needsMashing: true,
    description:
      'Soft white winter wheat, doughy and clean, turning bready in the boil. High extract, low enzyme, plenty of haze. Fine up to 50 %, and to 80 % with rice hulls.',
    tags: ['base', 'wheat', 'head-retention', 'haze', 'craft-malt', 'montana'],
  }),
  craftMalt({
    id: 'mcm-oat',
    name: 'Oat Malt',
    maker: 'Montana Craft Malt',
    type: 'adjunct-grain',
    maltClass: 'wheat',
    extractFineDryPercent: 58,
    moisturePercent: 6,
    colorLovibond: 2.8,
    diastaticPowerLintner: 25,
    suggestedMaxPercent: 30,
    needsMashing: true,
    description:
      'Malted oats for haze and a silky body, earthy and grainy. Extract is low because the hull stays on; keep it under 30 % and plan the lauter.',
    tags: ['oat', 'malted-oats', 'mouthfeel', 'hazy', 'craft-malt', 'montana'],
  }),
  craftMalt({
    id: 'mcm-pilsner',
    name: 'Pilsner',
    maker: 'Montana Craft Malt',
    type: 'base',
    maltClass: 'base-pilsner',
    extractFineDryPercent: 80,
    moisturePercent: 5,
    colorLovibond: 2.2,
    diastaticPowerLintner: 140,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'A North American pilsner, fully modified for a single infusion and kilned to drive off DMS. Yellow wort with straw and a little honey; high enzyme for a continental-named malt.',
    tags: ['base', 'pilsner', 'lager', 'craft-malt', 'montana', 'local'],
  }),
  craftMalt({
    id: 'mcm-2-row',
    name: '2-Row',
    maker: 'Montana Craft Malt',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 80,
    moisturePercent: 5,
    colorLovibond: 2.4,
    diastaticPowerLintner: 140,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'Montana two-row, a little higher in protein, color and enzyme than a typical pale, with a fast runoff. Sweet, grainy, honeyed wort for single-infusion ales and lagers.',
    tags: ['base', 'two-row', 'high-enzyme', 'craft-malt', 'montana', 'local'],
  }),
  // ---------------------------------------------------------------------------
  // Maverick Malt House — Wildorado, Texas
  // ---------------------------------------------------------------------------
  craftMalt({
    id: 'maverick-brewers-base-malt',
    name: 'Brewer’s Base Malt',
    maker: 'Maverick Malt House',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 81,
    moisturePercent: 4.5,
    colorLovibond: 2,
    diastaticPowerLintner: 120,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'Panhandle barley steeped cool and given a long germination, then kilned gently for a clean pale base. Maverick publishes no analysis, so the extract, moisture, color and diastatic figures here are typical of an American two-row pale rather than measured values of theirs.',
    tags: ['base', 'pale', 'texas', 'local', 'two-row'],
  }),
  craftMalt({
    id: 'maverick-distillers-base-malt',
    name: 'Distiller’s Base Malt',
    maker: 'Maverick Malt House',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 80,
    moisturePercent: 4.5,
    colorLovibond: 1.8,
    diastaticPowerLintner: 130,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'Built for mash conversion rather than color: cool steeping and a long germination raise the enzyme load, and the kilning is kept restrained to keep it. Aimed at distillers but usable as a very pale brewing base. No numbers are published, so these are enzyme-rich base malt norms, not Maverick’s own analysis.',
    tags: ['base', 'pale', 'texas', 'distilling', 'high-enzyme'],
  }),
  craftMalt({
    id: 'maverick-munich-malt',
    name: 'Munich Malt',
    maker: 'Maverick Malt House',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 79,
    moisturePercent: 4.5,
    colorLovibond: 10,
    diastaticPowerLintner: 40,
    suggestedMaxPercent: 60,
    needsMashing: true,
    description:
      'Kilned hot enough to build a round, bready sweetness while keeping enough enzyme to convert itself, so it can carry a large share of the grist. Maverick lists the malt but no analysis; the extract, moisture, color and diastatic power below are ordinary Munich figures standing in for theirs.',
    tags: ['munich', 'kilned', 'texas', 'malty', 'local'],
  }),
  craftMalt({
    id: 'maverick-vienna-malt',
    name: 'Vienna Malt',
    maker: 'Maverick Malt House',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 80,
    moisturePercent: 4.5,
    colorLovibond: 4,
    diastaticPowerLintner: 50,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'A half step past pale: enough kilning for body and a golden-to-amber cast, still enzymatic enough to stand as the whole base of a malty beer. The specification values given are class-typical Vienna numbers, since Maverick publishes none of its own.',
    tags: ['vienna', 'kilned', 'texas', 'amber', 'local'],
  }),
  craftMalt({
    id: 'maverick-wheat-malt',
    name: 'Wheat Malt',
    maker: 'Maverick Malt House',
    type: 'base',
    maltClass: 'wheat',
    extractFineDryPercent: 84,
    moisturePercent: 5.5,
    colorLovibond: 2.5,
    diastaticPowerLintner: 100,
    suggestedMaxPercent: 50,
    needsMashing: true,
    description:
      'Texas soft red winter wheat, malted and kilned lightly, giving a soft bready note and the protein that holds a head together. Maverick states the grain and the kilning but no analysis, so the figures here are usual wheat malt values rather than measurements of this malt.',
    tags: ['wheat', 'base', 'texas', 'head-retention', 'local'],
  }),
  // ---------------------------------------------------------------------------
  // Homestead Malting — Queen Creek, Arizona (the malting arm of Grain R&D)
  // ---------------------------------------------------------------------------
  craftMalt({
    id: 'homestead-bohemian-mesa',
    name: 'Bohemian Mesa',
    maker: 'Homestead Malting',
    type: 'base',
    maltClass: 'base-pilsner',
    extractFineDryPercent: 78.0,
    moisturePercent: 3.97,
    colorLovibond: 2.0,
    diastaticPowerLintner: 174,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'Arizona-grown Czech-style pilsen malt held deliberately low on protein modification — Kolbach 35.6 per cent — so a double or triple decoction still has body and foam to work with. The enzyme reserve is unusually deep for a pilsen malt, which is what carries an all-malt mash through three thermal rests.',
    tags: ['base', 'pilsner', 'czech', 'arizona', 'decoction'],
    url: 'https://cdn.shopify.com/s/files/1/0758/8646/1163/files/Bohemian_Mesa_Spec_Sheet.pdf',
    provenance: 'spec-sheet',
  }),
  craftMalt({
    id: 'homestead-arizona-heritage-pale-ale',
    name: 'Arizona Heritage',
    maker: 'Homestead Malting',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 80.5,
    moisturePercent: 4.0,
    colorLovibond: 3.0,
    diastaticPowerLintner: 135,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'Two-row pale ale malt kilned a shade past pilsner for bread-crust and light honey without pushing the wort toward amber. Well modified at 85 per cent friability, so a single infusion near 65 °C converts it without a protein rest. Figures are the maltster’s initial-production targets rather than a shipped lot.',
    tags: ['base', 'pale-ale', 'arizona', 'two-row', 'single-infusion'],
    url: 'https://cdn.shopify.com/s/files/1/0758/8646/1163/files/Arizona_Heritage_Pale_Ale_Spec_Sheet.pdf',
  }),
  craftMalt({
    id: 'homestead-desert-gold-pilsner',
    name: 'Desert Gold',
    maker: 'Homestead Malting',
    type: 'base',
    maltClass: 'base-pilsner',
    extractFineDryPercent: 80.5,
    moisturePercent: 4.0,
    colorLovibond: 1.7,
    diastaticPowerLintner: 130,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'The palest malt in the Homestead range, kilned gently to keep Maillard character out of the way of hops and lager yeast. Slightly under-modified against their pale ale malt, so a short protein rest pays off in an all-pilsner grist. Ninety-minute boil recommended for SMM. Homestead label these initial-production target specifications rather than a measured lot.',
    tags: ['base', 'pilsner', 'lager', 'arizona', 'helles'],
    url: 'https://cdn.shopify.com/s/files/1/0758/8646/1163/files/Desert_Gold_Pilsner_Spec_Sheet.pdf',
  }),
  craftMalt({
    id: 'homestead-distillers-malt',
    name: 'Distiller’s Malt',
    maker: 'Homestead Malting',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 78.0,
    moisturePercent: 3.97,
    colorLovibond: 2.0,
    diastaticPowerLintner: 174,
    suggestedMaxPercent: 20,
    needsMashing: true,
    description:
      'Floor-malted two-row Genie built for the still rather than the lauter: 174 °L of diastatic power and 207 mg/L FAN give room to run corn, rye or wheat up to roughly 80 per cent of the mash. Protein modification is low by brewing standards, which the maltster notes is deliberate and not a defect.',
    tags: ['base', 'distilling', 'high-diastatic', 'arizona', 'floor-malted'],
    url: 'https://cdn.shopify.com/s/files/1/0758/8646/1163/files/Distillers_Malt_Spec_Sheet.pdf',
    provenance: 'spec-sheet',
  }),
  craftMalt({
    id: 'homestead-padre-kino-white-sonora-wheat',
    name: 'Padre Kino',
    maker: 'Homestead Malting',
    type: 'base',
    maltClass: 'wheat',
    extractFineDryPercent: 81.2,
    moisturePercent: 5.6,
    colorLovibond: 1.9,
    diastaticPowerLintner: 150,
    suggestedMaxPercent: 50,
    needsMashing: true,
    description:
      'Heritage White Sonora soft wheat malted on the Sossaman farm. Nutty-sweet and mildly grainy, with enough diastatic strength to push wheat ratios past what conventional malted wheat allows. Beta-glucan at 130 mg/L means rice hulls above about a quarter of the grist. Published figures are projected pending a lab certificate.',
    tags: ['wheat', 'heritage', 'white-sonora', 'arizona', 'hefeweizen'],
    url: 'https://cdn.shopify.com/s/files/1/0758/8646/1163/files/Padre_Kino_Spec_Sheet.pdf',
  }),
  craftMalt({
    id: 'homestead-phoenix-pharaoh-emmer',
    name: 'Phoenix Pharaoh',
    maker: 'Homestead Malting',
    type: 'base',
    maltClass: 'wheat',
    extractFineDryPercent: 78.0,
    moisturePercent: 4.5,
    colorLovibond: 5.5,
    diastaticPowerLintner: 32,
    suggestedMaxPercent: 40,
    needsMashing: true,
    description:
      'Dehulled emmer malted on a wheat schedule, giving a nutty and grassy signature for saison and farmhouse work. Diastatic power is low at 32 °L, which reflects emmer’s own enzyme system, so pair it with a strong base malt above a quarter of the grist. Numbers are pilot targets, not a produced batch.',
    tags: ['wheat', 'emmer', 'ancient-grain', 'arizona', 'saison'],
    url: 'https://cdn.shopify.com/s/files/1/0758/8646/1163/files/Phoenix_Pharaoh_Spec_Sheet.pdf',
  }),
  craftMalt({
    id: 'homestead-white-sonora-pale-malt',
    name: 'White Sonora Pale Malt',
    maker: 'Homestead Malting',
    type: 'base',
    maltClass: 'wheat',
    extractFineDryPercent: 81.0,
    moisturePercent: 5.0,
    colorLovibond: 2.0,
    diastaticPowerLintner: 140,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'A White Sonora wheat taken to a genuine base grade rather than an inclusion malt, meant to anchor all-wheat and high-wheat grists. Homestead names and sells it but has not posted a spec sheet for it, so the extract, moisture, color and diastatic figures here are wheat-base typicals, not their published analysis.',
    tags: ['wheat', 'base', 'white-sonora', 'heritage', 'arizona'],
  }),
  craftMalt({
    id: 'homestead-ute-moonstone-blue-maize',
    name: 'Ute Moonstone',
    maker: 'Homestead Malting',
    type: 'adjunct-grain',
    maltClass: 'non-buffering',
    extractFineDryPercent: 82.0,
    moisturePercent: 12.5,
    colorLovibond: 1.5,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 40,
    needsMashing: true,
    description:
      'Organic heritage blue maize grown by the Ute Mountain Ute Tribal Farm and rough-cracked for cereal mashes and whiskey bills. Anthocyanin-rich kernels carry an earthy-sweet maize character and visible indigo. Moisture is Homestead’s published figure; extract and color here are corn-adjunct typicals, and raw maize contributes no diastatic power.',
    tags: ['adjunct', 'corn', 'blue-maize', 'heritage', 'distilling'],
    url: 'https://cdn.shopify.com/s/files/1/0758/8646/1163/files/Ute_Moonstone_Spec_Sheet.pdf',
  }),
  // ---------------------------------------------------------------------------
  // Gallatin Valley Malt Co. — Manhattan, Montana
  // ---------------------------------------------------------------------------
  craftMalt({
    id: 'gallatin-valley-gallatin-pale',
    name: 'Gallatin Pale Malt',
    maker: 'Gallatin Valley Malt',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 80.0,
    moisturePercent: 4.5,
    colorLovibond: 2.2,
    diastaticPowerLintner: 110,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'The flagship, made from the farm’s own Genie barley. Aromas run to pizza crust and warm bread, with toasted cereal and golden honey on the palate and a clean, faintly sweet finish. Gallatin Valley publish no analysis, so these figures are class typicals.',
    tags: ['base', 'pale', 'genie', 'montana', 'flagship'],
    url: 'https://www.gallatinvalleymalt.com/gallatin-pale',
  }),
  craftMalt({
    id: 'gallatin-valley-hyalite-pilsner',
    name: 'Hyalite Pilsner Malt',
    maker: 'Gallatin Valley Malt',
    type: 'base',
    maltClass: 'base-pilsner',
    extractFineDryPercent: 80.5,
    moisturePercent: 4.3,
    colorLovibond: 1.7,
    diastaticPowerLintner: 115,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'Kilned at low temperature under high airflow to keep it delicate — lighter and more refined than their pale, with biscuit, hay and wildflower honey and a bright straw-gold wort. Gallatin Valley publish no analysis, so these figures are class typicals.',
    tags: ['base', 'pilsner', 'montana', 'lager', 'low-kilned'],
    url: 'https://www.gallatinvalleymalt.com/hyalite-pilsner',
  }),
  craftMalt({
    id: 'gallatin-valley-camp-creek-pilsner',
    name: 'Camp Creek Pilsner Malt',
    maker: 'Gallatin Valley Malt',
    type: 'base',
    maltClass: 'base-pilsner',
    extractFineDryPercent: 80.0,
    moisturePercent: 4.3,
    colorLovibond: 1.9,
    diastaticPowerLintner: 110,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'A sweeter, more expressive pilsner than Hyalite thanks to a different barley variety. Toasted cereal and sun-dried hay on the nose, bread crust and wildflower honey underneath. Gallatin Valley publish no analysis, so these figures are class typicals.',
    tags: ['base', 'pilsner', 'montana', 'lager', 'malt-forward'],
    url: 'https://www.gallatinvalleymalt.com/gallatin-pale-copy',
  }),
  craftMalt({
    id: 'gallatin-valley-heritage-pilsner',
    name: 'Heritage Pilsner Malt',
    maker: 'Gallatin Valley Malt',
    type: 'base',
    maltClass: 'base-pilsner',
    extractFineDryPercent: 79.0,
    moisturePercent: 4.3,
    colorLovibond: 1.9,
    diastaticPowerLintner: 95,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'Made from Saale, a barley first grown in the late 1800s and brought back by Montana State University’s barley lab. Honeydew, cut hay and green tea give it a pre-Prohibition character that modern pilsner malts do not have. Gallatin Valley publish no analysis, so these figures are class typicals.',
    tags: ['base', 'pilsner', 'heritage', 'saale', 'montana'],
    url: 'https://www.gallatinvalleymalt.com/heritage-pilsner',
  }),
  craftMalt({
    id: 'gallatin-valley-keughen-vienna',
    name: 'Keughen Vienna Malt',
    maker: 'Gallatin Valley Malt',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 79.5,
    moisturePercent: 4.2,
    colorLovibond: 6.0,
    diastaticPowerLintner: 55,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'Two-time award-winning Vienna malt off their Genie barley, kilned to 5–6.5 SRM. Baked biscuit and toasted grain on the nose, then cereal, honey, buttery crackers and a touch of green tea. Gallatin Valley publish no analysis, so these figures are class typicals.',
    tags: ['vienna', 'kilned', 'montana', 'marzen', 'amber'],
    url: 'https://www.gallatinvalleymalt.com/keughen-vienna',
  }),
  craftMalt({
    id: 'gallatin-valley-madison-munich-srm-4',
    name: 'Madison Munich SRM 4',
    maker: 'Gallatin Valley Malt',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 79.0,
    moisturePercent: 4.2,
    colorLovibond: 4.0,
    diastaticPowerLintner: 60,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'Munich malt from the Madison series, kilned to 4 SRM — the lightest of the four, close to a dark pale malt in use. Color is the maltster’s published figure; the rest are Munich typicals.',
    tags: ['munich', 'kilned', 'montana', 'malt-forward', 'madison'],
  }),
  craftMalt({
    id: 'gallatin-valley-madison-munich-srm-10',
    name: 'Madison Munich SRM 10',
    maker: 'Gallatin Valley Malt',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 79.0,
    moisturePercent: 4.2,
    colorLovibond: 10.0,
    diastaticPowerLintner: 40,
    suggestedMaxPercent: 80,
    needsMashing: true,
    description:
      'Munich malt from the Madison series, kilned to 10 SRM — the classic Munich weight for helles and dunkel. Color is the maltster’s published figure; the rest are Munich typicals.',
    tags: ['munich', 'kilned', 'montana', 'malt-forward', 'madison'],
  }),
  craftMalt({
    id: 'gallatin-valley-madison-munich-srm-25',
    name: 'Madison Munich SRM 25',
    maker: 'Gallatin Valley Malt',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 76.0,
    moisturePercent: 4.2,
    colorLovibond: 25.0,
    diastaticPowerLintner: 20,
    suggestedMaxPercent: 40,
    needsMashing: true,
    description:
      'Munich malt from the Madison series, kilned to 25 SRM — a dark Munich, bready and deeply toasted. Color is the maltster’s published figure; the rest are Munich typicals.',
    tags: ['munich', 'kilned', 'montana', 'malt-forward', 'madison'],
  }),
  craftMalt({
    id: 'gallatin-valley-madison-munich-srm-60',
    name: 'Madison Munich SRM 60',
    maker: 'Gallatin Valley Malt',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 76.0,
    moisturePercent: 4.2,
    colorLovibond: 60.0,
    diastaticPowerLintner: 5,
    suggestedMaxPercent: 20,
    needsMashing: true,
    description:
      'Munich malt from the Madison series, kilned to 60 SRM — nearly a melanoidin malt, for color and depth in small doses. Color is the maltster’s published figure; the rest are Munich typicals.',
    tags: ['munich', 'kilned', 'montana', 'malt-forward', 'madison'],
  }),
  craftMalt({
    id: 'gallatin-valley-jefferson-white-wheat',
    name: 'Jefferson White Wheat Malt',
    maker: 'Gallatin Valley Malt',
    type: 'base',
    maltClass: 'wheat',
    extractFineDryPercent: 82.0,
    moisturePercent: 5.0,
    colorLovibond: 2.0,
    diastaticPowerLintner: 120,
    suggestedMaxPercent: 60,
    needsMashing: true,
    description:
      'One of the original Gallatin Valley malts, from white wheat grown on their Montana farm. Soft and pillowy in the mouth with bread dough, hay, honey and melon, and enough protein to hold a head and a haze. Gallatin Valley publish no analysis, so these figures are class typicals.',
    tags: ['wheat', 'base', 'montana', 'hefeweizen', 'haze'],
    url: 'https://www.gallatinvalleymalt.com/jefferson-white-wheat',
  }),
  craftMalt({
    id: 'gallatin-valley-amber-wheat',
    name: 'Amber Wheat Malt',
    maker: 'Gallatin Valley Malt',
    type: 'kilned-toasted',
    maltClass: 'wheat',
    extractFineDryPercent: 80.0,
    moisturePercent: 4.5,
    colorLovibond: 5.0,
    diastaticPowerLintner: 60,
    suggestedMaxPercent: 40,
    needsMashing: true,
    description:
      'Wheat malt kilned further than their white wheat to 5 SRM, giving a nuttier, richer profile and a golden-amber cast. Melon, wheat bread and gentle toast — good for dunkelweizen and malt-forward wheat beers. Gallatin Valley publish no analysis, so these figures are class typicals.',
    tags: ['wheat', 'kilned', 'amber', 'montana', 'dunkelweizen'],
    url: 'https://www.gallatinvalleymalt.com/amber-wheat',
  }),
  craftMalt({
    id: 'gallatin-valley-malted-rye',
    name: 'Malted Rye',
    maker: 'Gallatin Valley Malt',
    type: 'base',
    maltClass: 'wheat',
    extractFineDryPercent: 78.0,
    moisturePercent: 4.5,
    colorLovibond: 3.5,
    diastaticPowerLintner: 90,
    suggestedMaxPercent: 25,
    needsMashing: true,
    description:
      'Rye bread, earthy spice and toasted grain, with the caramelized sweetness rye picks up in the kiln. Adds body and a signature that carries through saisons, rye ales and dark styles alike. Gallatin Valley publish no analysis, so these figures are class typicals.',
    tags: ['rye', 'specialty', 'montana', 'saison', 'spicy'],
    url: 'https://www.gallatinvalleymalt.com/malted-rye',
  }),
  craftMalt({
    id: 'gallatin-valley-malted-oats',
    name: 'Malted Oats',
    maker: 'Gallatin Valley Malt',
    type: 'base',
    maltClass: 'wheat',
    extractFineDryPercent: 75.0,
    moisturePercent: 5.0,
    colorLovibond: 2.0,
    diastaticPowerLintner: 25,
    suggestedMaxPercent: 20,
    needsMashing: true,
    description:
      'Husked malted oats with live enzymes, so they can go straight into the mash. Creamy mouthfeel, oatmeal and light sweetness, and the husk keeps the lauter moving where flaked oats would not. Gallatin Valley publish no analysis, so these figures are class typicals.',
    tags: ['oats', 'specialty', 'montana', 'mouthfeel', 'hazy-ipa'],
    url: 'https://www.gallatinvalleymalt.com/malted-oats',
  }),
  craftMalt({
    id: 'gallatin-valley-malted-corn',
    name: 'Malted Corn',
    maker: 'Gallatin Valley Malt',
    type: 'adjunct-grain',
    maltClass: 'non-buffering',
    extractFineDryPercent: 82.0,
    moisturePercent: 5.0,
    colorLovibond: 1.5,
    diastaticPowerLintner: 25,
    suggestedMaxPercent: 30,
    needsMashing: true,
    description:
      'Dent corn taken through the malthouse rather than the flaker, so it arrives with its own enzymes and needs no cereal cook. Sweet corn and light hay, with very little color — built for cream ales and light lagers. Gallatin Valley publish no analysis, so these figures are class typicals.',
    tags: ['adjunct', 'corn', 'malted', 'montana', 'cream-ale'],
    url: 'https://www.gallatinvalleymalt.com/malted-corn',
  }),
  craftMalt({
    id: 'gallatin-valley-dextrin-malt',
    name: 'Dextrin Malt',
    maker: 'Gallatin Valley Malt',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 74.0,
    moisturePercent: 5.0,
    colorLovibond: 1.5,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 15,
    needsMashing: false,
    description:
      'Moravian barley kilned low and slow to add body, mouthfeel and foam stability while contributing almost no color. Largely unfermentable, with a soft cereal and light grassy character. Gallatin Valley publish no analysis, so these figures are class typicals.',
    tags: ['dextrin', 'body', 'head-retention', 'montana', 'moravian'],
    url: 'https://www.gallatinvalleymalt.com/dextrin',
  }),
  craftMalt({
    id: 'gallatin-valley-honey-malt-srm-10',
    name: 'Honey Malt SRM 10',
    maker: 'Gallatin Valley Malt',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 78.0,
    moisturePercent: 4.5,
    colorLovibond: 10.0,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 20,
    needsMashing: false,
    description:
      'Honey malt at 10 SRM — a stewed malt in the crystal family that brings honeyed sweetness and a soft, rounded middle without the raisin edge of dark caramel malts. Color is theirs; the remaining figures are typicals.',
    tags: ['honey', 'crystal', 'sweet', 'montana', 'specialty'],
  }),
  craftMalt({
    id: 'gallatin-valley-honey-malt-srm-20',
    name: 'Honey Malt SRM 20',
    maker: 'Gallatin Valley Malt',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 78.0,
    moisturePercent: 4.5,
    colorLovibond: 20.0,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 15,
    needsMashing: false,
    description:
      'Honey malt at 20 SRM — a stewed malt in the crystal family that brings honeyed sweetness and a soft, rounded middle without the raisin edge of dark caramel malts. Color is theirs; the remaining figures are typicals.',
    tags: ['honey', 'crystal', 'sweet', 'montana', 'specialty'],
  }),
  craftMalt({
    id: 'gallatin-valley-k-15-crystal',
    name: 'K-15 Crystal Malt',
    maker: 'Gallatin Valley Malt',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 77.0,
    moisturePercent: 5.0,
    colorLovibond: 15.0,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 20,
    needsMashing: false,
    description:
      'Part of the K-Series, named for an irrigation canal on the family farm. At 15 SRM it gives light caramel and a touch of honey. Color comes from the product name; extract, moisture and diastatic figures are crystal typicals.',
    tags: ['crystal', 'caramel', 'montana', 'k-series', 'barley'],
  }),
  craftMalt({
    id: 'gallatin-valley-k-30-crystal',
    name: 'K-30 Crystal Malt',
    maker: 'Gallatin Valley Malt',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 76.0,
    moisturePercent: 5.0,
    colorLovibond: 30.0,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 20,
    needsMashing: false,
    description:
      'Part of the K-Series, named for an irrigation canal on the family farm. At 30 SRM it gives light caramel and a touch of honey. Color comes from the product name; extract, moisture and diastatic figures are crystal typicals.',
    tags: ['crystal', 'caramel', 'montana', 'k-series', 'barley'],
  }),
  craftMalt({
    id: 'gallatin-valley-k-40-crystal',
    name: 'K-40 Crystal Malt',
    maker: 'Gallatin Valley Malt',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 76.0,
    moisturePercent: 5.0,
    colorLovibond: 40.0,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 20,
    needsMashing: false,
    description:
      'Part of the K-Series, named for an irrigation canal on the family farm. At 40 SRM it gives light caramel and a touch of honey. Color comes from the product name; extract, moisture and diastatic figures are crystal typicals.',
    tags: ['crystal', 'caramel', 'montana', 'k-series', 'barley'],
  }),
  craftMalt({
    id: 'gallatin-valley-k-60-crystal',
    name: 'K-60 Crystal Malt',
    maker: 'Gallatin Valley Malt',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 75.0,
    moisturePercent: 5.0,
    colorLovibond: 60.0,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 15,
    needsMashing: false,
    description:
      'Part of the K-Series, named for an irrigation canal on the family farm. At 60 SRM it gives toffee, caramel and light stone fruit. Color comes from the product name; extract, moisture and diastatic figures are crystal typicals.',
    tags: ['crystal', 'caramel', 'montana', 'k-series', 'barley'],
  }),
  craftMalt({
    id: 'gallatin-valley-k-90-crystal',
    name: 'K-90 Crystal Malt',
    maker: 'Gallatin Valley Malt',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 74.0,
    moisturePercent: 5.0,
    colorLovibond: 90.0,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 12,
    needsMashing: false,
    description:
      'Part of the K-Series, named for an irrigation canal on the family farm. At 90 SRM it gives toffee, caramel and light stone fruit. Color comes from the product name; extract, moisture and diastatic figures are crystal typicals.',
    tags: ['crystal', 'caramel', 'montana', 'k-series', 'barley'],
  }),
  craftMalt({
    id: 'gallatin-valley-k-120-crystal',
    name: 'K-120 Crystal Malt',
    maker: 'Gallatin Valley Malt',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 73.0,
    moisturePercent: 5.0,
    colorLovibond: 120.0,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: false,
    description:
      'Part of the K-Series, named for an irrigation canal on the family farm. At 120 SRM it gives raisin, dark caramel and a hint of burnt sugar. Color comes from the product name; extract, moisture and diastatic figures are crystal typicals.',
    tags: ['crystal', 'caramel', 'montana', 'k-series', 'barley'],
  }),
  craftMalt({
    id: 'gallatin-valley-k-200-crystal',
    name: 'K-200 Crystal Malt',
    maker: 'Gallatin Valley Malt',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 71.0,
    moisturePercent: 5.0,
    colorLovibond: 200.0,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 7,
    needsMashing: false,
    description:
      'Part of the K-Series, named for an irrigation canal on the family farm. At 200 SRM it gives raisin, dark caramel and a hint of burnt sugar. Color comes from the product name; extract, moisture and diastatic figures are crystal typicals.',
    tags: ['crystal', 'caramel', 'montana', 'k-series', 'barley'],
  }),
  craftMalt({
    id: 'gallatin-valley-crystal-wheat-srm-20',
    name: 'Crystal Wheat SRM 20',
    maker: 'Gallatin Valley Malt',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 75.0,
    moisturePercent: 5.0,
    colorLovibond: 20.0,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 15,
    needsMashing: false,
    description:
      'Wheat stewed and dried like a crystal barley malt, at 20 SRM. Caramel sweetness with the softer, doughier edge wheat brings, and it lifts head retention at the same time. Gallatin Valley publish no analysis, so these figures are class typicals.',
    tags: ['crystal', 'wheat', 'caramel', 'montana', 'head-retention'],
  }),
  craftMalt({
    id: 'gallatin-valley-crystal-wheat-srm-35',
    name: 'Crystal Wheat SRM 35',
    maker: 'Gallatin Valley Malt',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 75.0,
    moisturePercent: 5.0,
    colorLovibond: 35.0,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 12,
    needsMashing: false,
    description:
      'Wheat stewed and dried like a crystal barley malt, at 35 SRM. Caramel sweetness with the softer, doughier edge wheat brings, and it lifts head retention at the same time. Gallatin Valley publish no analysis, so these figures are class typicals.',
    tags: ['crystal', 'wheat', 'caramel', 'montana', 'head-retention'],
  }),
  craftMalt({
    id: 'gallatin-valley-crystal-rye-srm-40',
    name: 'Crystal Rye SRM 40',
    maker: 'Gallatin Valley Malt',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 74.0,
    moisturePercent: 5.0,
    colorLovibond: 40.0,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 12,
    needsMashing: false,
    description:
      'Crystal rye at 40 SRM, pairing stewed caramel sweetness with rye’s peppery, rustic bite. Useful where a straight caramel malt would read as too soft. Gallatin Valley publish no analysis, so these figures are class typicals.',
    tags: ['crystal', 'rye', 'caramel', 'montana', 'spicy'],
  }),
  craftMalt({
    id: 'gallatin-valley-crystal-rye-srm-100',
    name: 'Crystal Rye SRM 100',
    maker: 'Gallatin Valley Malt',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 74.0,
    moisturePercent: 5.0,
    colorLovibond: 100.0,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: false,
    description:
      'Crystal rye at 100 SRM, pairing stewed caramel sweetness with rye’s peppery, rustic bite. Useful where a straight caramel malt would read as too soft. Gallatin Valley publish no analysis, so these figures are class typicals.',
    tags: ['crystal', 'rye', 'caramel', 'montana', 'spicy'],
  }),
  craftMalt({
    id: 'gallatin-valley-crystal-rye-srm-140',
    name: 'Crystal Rye SRM 140',
    maker: 'Gallatin Valley Malt',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 74.0,
    moisturePercent: 5.0,
    colorLovibond: 140.0,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 8,
    needsMashing: false,
    description:
      'Crystal rye at 140 SRM, pairing stewed caramel sweetness with rye’s peppery, rustic bite. Useful where a straight caramel malt would read as too soft. Gallatin Valley publish no analysis, so these figures are class typicals.',
    tags: ['crystal', 'rye', 'caramel', 'montana', 'spicy'],
  }),
  craftMalt({
    id: 'gallatin-valley-biscuit-malt',
    name: 'Biscuit Malt',
    maker: 'Gallatin Valley Malt',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 76.0,
    moisturePercent: 3.5,
    colorLovibond: 20.0,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 15,
    needsMashing: true,
    description:
      'A 20 SRM toasted malt that gives grain character without sweetness — toast, multigrain cracker and raw almond over a dry, rustic base. Good for brown and amber ales and Belgian styles wanting bread-crust depth. Gallatin Valley publish no analysis, so these figures are class typicals.',
    tags: ['biscuit', 'toasted', 'montana', 'brown-ale', 'nutty'],
    url: 'https://www.gallatinvalleymalt.com/biscuit-malt',
  }),
  craftMalt({
    id: 'gallatin-valley-frontier-malt',
    name: 'Frontier Malt',
    maker: 'Gallatin Valley Malt',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 72.0,
    moisturePercent: 3.0,
    colorLovibond: 130.0,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: false,
    description:
      'Award-winning specialty malt made from Genie barley and finished in the roaster. At around 130 SRM it layers cherry, toffee, toasted marshmallow and vanilla over a deep brown color, adding depth without flattening the base malt. Gallatin Valley publish no analysis, so these figures are class typicals.',
    tags: ['roasted', 'specialty', 'montana', 'porter', 'toffee'],
    url: 'https://www.gallatinvalleymalt.com/frontier-malt',
  }),
  craftMalt({
    id: 'gallatin-valley-naked-ash-barley',
    name: 'Naked Ash Barley',
    maker: 'Gallatin Valley Malt',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 68.0,
    moisturePercent: 3.0,
    colorLovibond: 350.0,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 8,
    needsMashing: false,
    description:
      'A 350 SRM roast from MT Kidd hulless barley. Losing the husk takes the harsh tannins out of the roast, leaving sweet cigar, coffee grounds and ash rather than the astringency a roasted barley usually carries. Gallatin Valley publish no analysis, so these figures are class typicals.',
    tags: ['roasted', 'hulless', 'montana', 'stout', 'coffee'],
    url: 'https://www.gallatinvalleymalt.com/naked-ash-barley',
  }),
  craftMalt({
    id: 'gallatin-valley-brown-malt-srm-60',
    name: 'Brown Malt SRM 60',
    maker: 'Gallatin Valley Malt',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 72.0,
    moisturePercent: 3.0,
    colorLovibond: 60.0,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 12,
    needsMashing: false,
    description:
      'Brown malt at 60 SRM, sitting between a dark crystal and a chocolate malt — dry biscuit, coffee and light roast, the backbone of a traditional porter. Gallatin Valley publish no analysis, so these figures are class typicals.',
    tags: ['roasted', 'brown-malt', 'montana', 'porter', 'dry'],
  }),
  craftMalt({
    id: 'gallatin-valley-brown-malt-srm-90',
    name: 'Brown Malt SRM 90',
    maker: 'Gallatin Valley Malt',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 72.0,
    moisturePercent: 3.0,
    colorLovibond: 90.0,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: false,
    description:
      'Brown malt at 90 SRM, sitting between a dark crystal and a chocolate malt — dry biscuit, coffee and light roast, the backbone of a traditional porter. Gallatin Valley publish no analysis, so these figures are class typicals.',
    tags: ['roasted', 'brown-malt', 'montana', 'porter', 'dry'],
  }),
  craftMalt({
    id: 'gallatin-valley-chocolate-malt-srm-200',
    name: 'Chocolate Malt SRM 200',
    maker: 'Gallatin Valley Malt',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 70.0,
    moisturePercent: 3.0,
    colorLovibond: 200.0,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: false,
    description:
      'Chocolate malt roasted to 200 SRM. The lighter end of the series reads as cocoa and nut, the darker end as espresso and dark chocolate with a firmer roast bite. Gallatin Valley publish no analysis, so these figures are class typicals.',
    tags: ['roasted', 'chocolate', 'montana', 'stout', 'cocoa'],
  }),
  craftMalt({
    id: 'gallatin-valley-chocolate-malt-srm-300',
    name: 'Chocolate Malt SRM 300',
    maker: 'Gallatin Valley Malt',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 70.0,
    moisturePercent: 3.0,
    colorLovibond: 300.0,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 8,
    needsMashing: false,
    description:
      'Chocolate malt roasted to 300 SRM. The lighter end of the series reads as cocoa and nut, the darker end as espresso and dark chocolate with a firmer roast bite. Gallatin Valley publish no analysis, so these figures are class typicals.',
    tags: ['roasted', 'chocolate', 'montana', 'stout', 'cocoa'],
  }),
  craftMalt({
    id: 'gallatin-valley-chocolate-malt-srm-350',
    name: 'Chocolate Malt SRM 350',
    maker: 'Gallatin Valley Malt',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 68.0,
    moisturePercent: 3.0,
    colorLovibond: 350.0,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 7,
    needsMashing: false,
    description:
      'Chocolate malt roasted to 350 SRM. The lighter end of the series reads as cocoa and nut, the darker end as espresso and dark chocolate with a firmer roast bite. Gallatin Valley publish no analysis, so these figures are class typicals.',
    tags: ['roasted', 'chocolate', 'montana', 'stout', 'cocoa'],
  }),
  craftMalt({
    id: 'gallatin-valley-chocolate-malt-srm-400',
    name: 'Chocolate Malt SRM 400',
    maker: 'Gallatin Valley Malt',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 68.0,
    moisturePercent: 3.0,
    colorLovibond: 400.0,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 6,
    needsMashing: false,
    description:
      'Chocolate malt roasted to 400 SRM. The lighter end of the series reads as cocoa and nut, the darker end as espresso and dark chocolate with a firmer roast bite. Gallatin Valley publish no analysis, so these figures are class typicals.',
    tags: ['roasted', 'chocolate', 'montana', 'stout', 'cocoa'],
  }),
  craftMalt({
    id: 'gallatin-valley-chocolate-malt-srm-500',
    name: 'Chocolate Malt SRM 500',
    maker: 'Gallatin Valley Malt',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 68.0,
    moisturePercent: 3.0,
    colorLovibond: 500.0,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 5,
    needsMashing: false,
    description:
      'Chocolate malt roasted to 500 SRM. The lighter end of the series reads as cocoa and nut, the darker end as espresso and dark chocolate with a firmer roast bite. Gallatin Valley publish no analysis, so these figures are class typicals.',
    tags: ['roasted', 'chocolate', 'montana', 'stout', 'cocoa'],
  }),
  craftMalt({
    id: 'gallatin-valley-west-fork-roasted-wheat-srm-200',
    name: 'West Fork Roasted Wheat SRM 200',
    maker: 'Gallatin Valley Malt',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 70.0,
    moisturePercent: 3.0,
    colorLovibond: 200.0,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 8,
    needsMashing: false,
    description:
      'Roasted wheat at 200 SRM. Huskless, so it darkens a beer and adds roast depth with far less astringency than roasted barley, and it holds a head while doing it. Gallatin Valley publish no analysis, so these figures are class typicals.',
    tags: ['roasted', 'wheat', 'montana', 'smooth', 'dark'],
  }),
  craftMalt({
    id: 'gallatin-valley-west-fork-roasted-wheat-srm-350',
    name: 'West Fork Roasted Wheat SRM 350',
    maker: 'Gallatin Valley Malt',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 70.0,
    moisturePercent: 3.0,
    colorLovibond: 350.0,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 6,
    needsMashing: false,
    description:
      'Roasted wheat at 350 SRM. Huskless, so it darkens a beer and adds roast depth with far less astringency than roasted barley, and it holds a head while doing it. Gallatin Valley publish no analysis, so these figures are class typicals.',
    tags: ['roasted', 'wheat', 'montana', 'smooth', 'dark'],
  }),
  craftMalt({
    id: 'gallatin-valley-west-fork-roasted-wheat-srm-500',
    name: 'West Fork Roasted Wheat SRM 500',
    maker: 'Gallatin Valley Malt',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 70.0,
    moisturePercent: 3.0,
    colorLovibond: 500.0,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 5,
    needsMashing: false,
    description:
      'Roasted wheat at 500 SRM. Huskless, so it darkens a beer and adds roast depth with far less astringency than roasted barley, and it holds a head while doing it. Gallatin Valley publish no analysis, so these figures are class typicals.',
    tags: ['roasted', 'wheat', 'montana', 'smooth', 'dark'],
  }),
  // ---------------------------------------------------------------------------
  // Solstice Malt — Salt Lake City, Utah
  // ---------------------------------------------------------------------------
  craftMalt({
    id: 'solstice-pilsner-malt',
    name: 'Pilsner Malt',
    maker: 'Solstice Malt',
    type: 'base',
    maltClass: 'base-pilsner',
    extractFineDryPercent: 80.5,
    moisturePercent: 4.5,
    colorLovibond: 1.8,
    diastaticPowerLintner: 110,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'The lightest malt Solstice make, quoted at 1.5–2 SRM, with grassy and grainy notes. Floor-malted in Salt Lake City from Utah-grown barley. Solstice publish product notes but no analysis; extract, moisture and diastatic figures here are class typicals.',
    tags: ['base', 'pilsner', 'utah', 'floor-malted', 'lager'],
  }),
  craftMalt({
    id: 'solstice-pale-malt',
    name: 'Pale Malt',
    maker: 'Solstice Malt',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 80.0,
    moisturePercent: 4.5,
    colorLovibond: 2.8,
    diastaticPowerLintner: 105,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'Solstice’s most popular malt, quoted at 2.5–3 SRM. Light and clean, meant as the default base for most of the Utah breweries they supply. Solstice publish product notes but no analysis; extract, moisture and diastatic figures here are class typicals.',
    tags: ['base', 'pale', 'utah', 'floor-malted', 'ale'],
  }),
  craftMalt({
    id: 'solstice-mild-malt',
    name: 'Mild Malt',
    maker: 'Solstice Malt',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 79.5,
    moisturePercent: 4.5,
    colorLovibond: 5.5,
    diastaticPowerLintner: 70,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'A 5–6 SRM base malt the maltster describes as a personal favorite and a workable stand-in for Vienna. Warmer and more bready than their pale without moving into Munich territory. Solstice publish product notes but no analysis; extract, moisture and diastatic figures here are class typicals.',
    tags: ['base', 'mild', 'utah', 'vienna-substitute', 'floor-malted'],
  }),
  craftMalt({
    id: 'solstice-munich-malt',
    name: 'Munich Malt',
    maker: 'Solstice Malt',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 78.0,
    moisturePercent: 4.2,
    colorLovibond: 11.0,
    diastaticPowerLintner: 35,
    suggestedMaxPercent: 60,
    needsMashing: true,
    description:
      'Munich malt at a quoted 10–12 SRM, rich and malt-forward. Color is Solstice’s published range; the rest are Munich typicals. Solstice publish product notes but no analysis; extract, moisture and diastatic figures here are class typicals.',
    tags: ['munich', 'kilned', 'utah', 'malt-forward', 'floor-malted'],
  }),
  craftMalt({
    id: 'solstice-chit-malt',
    name: 'Chit Malt',
    maker: 'Solstice Malt',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 76.0,
    moisturePercent: 5.0,
    colorLovibond: 1.5,
    diastaticPowerLintner: 40,
    suggestedMaxPercent: 5,
    needsMashing: true,
    description:
      'An under-modified malt in the German tradition, used at three to five per cent of the grist to improve head retention rather than for extract. Solstice give the use rate but no analysis. Solstice publish product notes but no analysis; extract, moisture and diastatic figures here are class typicals.',
    tags: ['chit', 'undermodified', 'head-retention', 'utah', 'german'],
  }),
  craftMalt({
    id: 'solstice-egyptian-purple-malt',
    name: 'Egyptian Purple Malt',
    maker: 'Solstice Malt',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 76.0,
    moisturePercent: 4.8,
    colorLovibond: 4.0,
    diastaticPowerLintner: 55,
    suggestedMaxPercent: 40,
    needsMashing: true,
    description:
      'A hulless ancient purple barley, malted slightly under-modified and carrying just enough enzyme to convert itself. Solstice warn it is not a beginner’s malt, and the missing husk makes lautering the main thing to plan for. Solstice publish product notes but no analysis; extract, moisture and diastatic figures here are class typicals.',
    tags: ['base', 'purple-barley', 'heritage', 'hulless', 'utah'],
  }),
  craftMalt({
    id: 'solstice-rye-malt',
    name: 'Rye Malt',
    maker: 'Solstice Malt',
    type: 'base',
    maltClass: 'wheat',
    extractFineDryPercent: 78.0,
    moisturePercent: 4.5,
    colorLovibond: 3.0,
    diastaticPowerLintner: 90,
    suggestedMaxPercent: 25,
    needsMashing: true,
    description:
      'Lightly kilned Haslett rye, certified Utah organic. Peppery and rustic, and as with any malted rye the beta-glucans make rice hulls worth having on hand. Solstice publish product notes but no analysis; extract, moisture and diastatic figures here are class typicals.',
    tags: ['rye', 'organic', 'utah', 'haslett', 'spicy'],
  }),
  craftMalt({
    id: 'solstice-wheat-malt',
    name: 'Wheat Malt',
    maker: 'Solstice Malt',
    type: 'base',
    maltClass: 'wheat',
    extractFineDryPercent: 82.0,
    moisturePercent: 5.0,
    colorLovibond: 2.2,
    diastaticPowerLintner: 120,
    suggestedMaxPercent: 60,
    needsMashing: true,
    description:
      'Hard winter red wheat, lightly kilned. Solstice aim it at wheat beers; it also lends protein for foam and haze in anything hazy or soft. Solstice publish product notes but no analysis; extract, moisture and diastatic figures here are class typicals.',
    tags: ['wheat', 'base', 'utah', 'hefeweizen', 'red-wheat'],
  }),
  craftMalt({
    id: 'solstice-wind-malt',
    name: 'Wind Malt',
    maker: 'Solstice Malt',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 79.0,
    moisturePercent: 5.0,
    colorLovibond: 2.5,
    diastaticPowerLintner: 100,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'Kilned on summer heat and airflow rather than fuel. Because the cure runs cooler than a conventional kiln, Solstice recommend a ninety-minute boil to drive off DMS and its precursor. Solstice publish product notes but no analysis; extract, moisture and diastatic figures here are class typicals.',
    tags: ['base', 'pale', 'utah', 'wind-kilned', 'sustainable'],
  }),
  craftMalt({
    id: 'solstice-biscuit-malt',
    name: 'Biscuit Malt',
    maker: 'Solstice Malt',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 76.0,
    moisturePercent: 3.5,
    colorLovibond: 25.0,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 12,
    needsMashing: true,
    description:
      'Their Munich malt toasted to around 300 °F to push the aromatics further. Made to order rather than held in stock, which is unusual and worth knowing when planning a brew. Solstice publish product notes but no analysis; extract, moisture and diastatic figures here are class typicals.',
    tags: ['biscuit', 'toasted', 'utah', 'made-to-order', 'nutty'],
  }),
  craftMalt({
    id: 'solstice-crystal-35',
    name: 'Crystal 35',
    maker: 'Solstice Malt',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 76.0,
    moisturePercent: 5.0,
    colorLovibond: 35.0,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 18,
    needsMashing: false,
    description:
      'Crystal malt at a published 35 SRM, giving mild caramel. Color is Solstice’s figure; extract and moisture here are crystal typicals.',
    tags: ['crystal', 'caramel', 'utah', 'sweet', 'specialty'],
  }),
  craftMalt({
    id: 'solstice-crystal-77',
    name: 'Crystal 77',
    maker: 'Solstice Malt',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 74.0,
    moisturePercent: 5.0,
    colorLovibond: 77.0,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 12,
    needsMashing: false,
    description:
      'Crystal malt at a published 77 SRM, giving deeper caramel and toffee. Color is Solstice’s figure; extract and moisture here are crystal typicals.',
    tags: ['crystal', 'caramel', 'utah', 'sweet', 'specialty'],
  }),
  craftMalt({
    id: 'solstice-crystal-120',
    name: 'Crystal 120',
    maker: 'Solstice Malt',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 73.0,
    moisturePercent: 5.0,
    colorLovibond: 120.0,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: false,
    description:
      'Crystal malt at a published 120 SRM, giving dark caramel and raisin. Color is Solstice’s figure; extract and moisture here are crystal typicals.',
    tags: ['crystal', 'caramel', 'utah', 'sweet', 'specialty'],
  }),
  craftMalt({
    id: 'solstice-chocolate-malt',
    name: 'Chocolate Malt',
    maker: 'Solstice Malt',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 70.0,
    moisturePercent: 3.0,
    colorLovibond: 265.0,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 8,
    needsMashing: false,
    description:
      'Roasted to a published 265 SRM for rich cocoa character. Color is theirs; extract and moisture are roast typicals. Solstice publish product notes but no analysis; extract, moisture and diastatic figures here are class typicals.',
    tags: ['roasted', 'chocolate', 'utah', 'stout', 'cocoa'],
  }),
  craftMalt({
    id: 'solstice-midnight-wheat',
    name: 'Midnight Wheat',
    maker: 'Solstice Malt',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 68.0,
    moisturePercent: 3.0,
    colorLovibond: 400.0,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 6,
    needsMashing: false,
    description:
      'A 400 SRM roasted wheat that darkens a beer hard without the bitterness a roasted barley of the same color would bring. Huskless, so the roast stays smooth. Solstice publish product notes but no analysis; extract, moisture and diastatic figures here are class typicals.',
    tags: ['roasted', 'wheat', 'utah', 'smooth', 'dark'],
  }),
  craftMalt({
    id: 'solstice-apple-smoked-malt',
    name: 'Apple Smoked Malt',
    maker: 'Solstice Malt',
    type: 'kilned-toasted',
    maltClass: 'base-pale',
    extractFineDryPercent: 79.0,
    moisturePercent: 4.5,
    colorLovibond: 3.0,
    diastaticPowerLintner: 60,
    suggestedMaxPercent: 40,
    needsMashing: true,
    description:
      'Base malt smoked over applewood for a subtly sweet smoke. Solstice smoke in-house over fruit woods rather than beech, which is what separates this range from a German Rauchmalz. Solstice publish product notes but no analysis; extract, moisture and diastatic figures here are class typicals.',
    tags: ['smoked', 'fruitwood', 'utah', 'specialty', 'rauchbier'],
  }),
  craftMalt({
    id: 'solstice-apricot-smoked-malt',
    name: 'Apricot Smoked Malt',
    maker: 'Solstice Malt',
    type: 'kilned-toasted',
    maltClass: 'base-pale',
    extractFineDryPercent: 79.0,
    moisturePercent: 4.5,
    colorLovibond: 3.0,
    diastaticPowerLintner: 60,
    suggestedMaxPercent: 40,
    needsMashing: true,
    description:
      'Base malt smoked over apricot wood for a delicate, fruity smoke. Solstice smoke in-house over fruit woods rather than beech, which is what separates this range from a German Rauchmalz. Solstice publish product notes but no analysis; extract, moisture and diastatic figures here are class typicals.',
    tags: ['smoked', 'fruitwood', 'utah', 'specialty', 'rauchbier'],
  }),
  craftMalt({
    id: 'solstice-cherry-smoked-malt',
    name: 'Cherry Smoked Malt',
    maker: 'Solstice Malt',
    type: 'kilned-toasted',
    maltClass: 'base-pale',
    extractFineDryPercent: 79.0,
    moisturePercent: 4.5,
    colorLovibond: 3.0,
    diastaticPowerLintner: 60,
    suggestedMaxPercent: 40,
    needsMashing: true,
    description:
      'Base malt smoked over cherry wood for a warm, rounded cherrywood smoke. Solstice smoke in-house over fruit woods rather than beech, which is what separates this range from a German Rauchmalz. Solstice publish product notes but no analysis; extract, moisture and diastatic figures here are class typicals.',
    tags: ['smoked', 'fruitwood', 'utah', 'specialty', 'rauchbier'],
  }),
  craftMalt({
    id: 'solstice-pear-smoked-malt',
    name: 'Pear Smoked Malt',
    maker: 'Solstice Malt',
    type: 'kilned-toasted',
    maltClass: 'base-pale',
    extractFineDryPercent: 79.0,
    moisturePercent: 4.5,
    colorLovibond: 3.0,
    diastaticPowerLintner: 60,
    suggestedMaxPercent: 40,
    needsMashing: true,
    description:
      'Base malt smoked over pear wood for an unusual, gentle orchard smoke. Solstice smoke in-house over fruit woods rather than beech, which is what separates this range from a German Rauchmalz. Solstice publish product notes but no analysis; extract, moisture and diastatic figures here are class typicals.',
    tags: ['smoked', 'fruitwood', 'utah', 'specialty', 'rauchbier'],
  }),
  craftMalt({
    id: 'solstice-plum-smoked-malt',
    name: 'Plum Smoked Malt',
    maker: 'Solstice Malt',
    type: 'kilned-toasted',
    maltClass: 'base-pale',
    extractFineDryPercent: 79.0,
    moisturePercent: 4.5,
    colorLovibond: 3.0,
    diastaticPowerLintner: 60,
    suggestedMaxPercent: 40,
    needsMashing: true,
    description:
      'Base malt smoked over plum wood for a light sweet smoke, softer than the apple. Solstice smoke in-house over fruit woods rather than beech, which is what separates this range from a German Rauchmalz. Solstice publish product notes but no analysis; extract, moisture and diastatic figures here are class typicals.',
    tags: ['smoked', 'fruitwood', 'utah', 'specialty', 'rauchbier'],
  }),
  craftMalt({
    id: 'solstice-irish-peat-malt',
    name: 'Irish Peat Malt',
    maker: 'Solstice Malt',
    type: 'kilned-toasted',
    maltClass: 'base-pale',
    extractFineDryPercent: 79.0,
    moisturePercent: 4.5,
    colorLovibond: 3.0,
    diastaticPowerLintner: 55,
    suggestedMaxPercent: 15,
    needsMashing: true,
    description:
      'Grain smoked over imported Irish peat for an intense, earthy phenolic character. Peat is far more assertive than fruit wood, so a small share of the grist goes a long way. Solstice publish product notes but no analysis; extract, moisture and diastatic figures here are class typicals.',
    tags: ['smoked', 'peat', 'utah', 'phenolic', 'whisky'],
  }),
  craftMalt({
    id: 'solstice-uinta-10k-peat-smoked-malt',
    name: 'Uinta 10k Peat Smoked Malt',
    maker: 'Solstice Malt',
    type: 'kilned-toasted',
    maltClass: 'base-pale',
    extractFineDryPercent: 79.0,
    moisturePercent: 4.5,
    colorLovibond: 3.0,
    diastaticPowerLintner: 55,
    suggestedMaxPercent: 15,
    needsMashing: true,
    description:
      'Malt smoked over peat cut from a lake at ten thousand feet in Utah’s Uinta range — a genuinely local peat rather than an imported Scottish or Irish one, and the reason this malt exists. Solstice publish product notes but no analysis; extract, moisture and diastatic figures here are class typicals.',
    tags: ['smoked', 'peat', 'utah', 'uinta', 'local'],
  }),
  craftMalt({
    id: 'solstice-flaked-barley',
    name: 'Flaked Barley',
    maker: 'Solstice Malt',
    type: 'adjunct-grain',
    maltClass: 'non-buffering',
    extractFineDryPercent: 78.0,
    moisturePercent: 8.0,
    colorLovibond: 1.7,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 20,
    needsMashing: true,
    description:
      'Flaked Barley from Solstice, rolled rather than malted, contributing a rich grainy flavor and lasting foam. It carries no enzymes of its own, so it needs a diastatic base malt alongside it. Solstice publish product notes but no analysis; extract, moisture and diastatic figures here are class typicals.',
    tags: ['adjunct', 'flaked', 'barley', 'utah', 'unmalted'],
  }),
  craftMalt({
    id: 'solstice-flaked-corn',
    name: 'Flaked Corn',
    maker: 'Solstice Malt',
    type: 'adjunct-grain',
    maltClass: 'non-buffering',
    extractFineDryPercent: 83.0,
    moisturePercent: 8.0,
    colorLovibond: 1.0,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 30,
    needsMashing: true,
    description:
      'Flaked Corn from Solstice, rolled rather than malted, contributing a light body and dry, clean finish. It carries no enzymes of its own, so it needs a diastatic base malt alongside it. Solstice publish product notes but no analysis; extract, moisture and diastatic figures here are class typicals.',
    tags: ['adjunct', 'flaked', 'corn', 'utah', 'unmalted'],
  }),
  craftMalt({
    id: 'solstice-flaked-oats',
    name: 'Flaked Oats',
    maker: 'Solstice Malt',
    type: 'adjunct-grain',
    maltClass: 'non-buffering',
    extractFineDryPercent: 75.0,
    moisturePercent: 8.0,
    colorLovibond: 1.5,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 25,
    needsMashing: true,
    description:
      'Flaked Oats from Solstice, rolled rather than malted, contributing a silky texture, hull included for lauter grip. It carries no enzymes of its own, so it needs a diastatic base malt alongside it. Solstice publish product notes but no analysis; extract, moisture and diastatic figures here are class typicals.',
    tags: ['adjunct', 'flaked', 'oats', 'utah', 'unmalted'],
  }),
  craftMalt({
    id: 'solstice-flaked-rye',
    name: 'Flaked Rye',
    maker: 'Solstice Malt',
    type: 'adjunct-grain',
    maltClass: 'non-buffering',
    extractFineDryPercent: 76.0,
    moisturePercent: 8.0,
    colorLovibond: 2.8,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 15,
    needsMashing: true,
    description:
      'Flaked Rye from Solstice, rolled rather than malted, contributing a spicy complexity from organic Hazlet rye. It carries no enzymes of its own, so it needs a diastatic base malt alongside it. Solstice publish product notes but no analysis; extract, moisture and diastatic figures here are class typicals.',
    tags: ['adjunct', 'flaked', 'rye', 'utah', 'unmalted'],
  }),
  craftMalt({
    id: 'solstice-flaked-wheat',
    name: 'Flaked Wheat',
    maker: 'Solstice Malt',
    type: 'adjunct-grain',
    maltClass: 'non-buffering',
    extractFineDryPercent: 80.0,
    moisturePercent: 8.0,
    colorLovibond: 1.6,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 30,
    needsMashing: true,
    description:
      'Flaked Wheat from Solstice, rolled rather than malted, contributing protein for haze and head in wheat beers. It carries no enzymes of its own, so it needs a diastatic base malt alongside it. Solstice publish product notes but no analysis; extract, moisture and diastatic figures here are class typicals.',
    tags: ['adjunct', 'flaked', 'wheat', 'utah', 'unmalted'],
  }),
  craftMalt({
    id: 'solstice-raw-barley',
    name: 'Raw Barley',
    maker: 'Solstice Malt',
    type: 'adjunct-grain',
    maltClass: 'non-buffering',
    extractFineDryPercent: 72.0,
    moisturePercent: 11.0,
    colorLovibond: 1.8,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 15,
    needsMashing: true,
    description:
      'Unprocessed barley offered alongside the malted range, giving grainy dryness and firm head retention. Raw grain needs a cereal mash or a strongly diastatic base malt to convert, and rice hulls help with the sticky ones. Solstice publish product notes but no analysis; extract, moisture and diastatic figures here are class typicals.',
    tags: ['adjunct', 'raw-grain', 'barley', 'utah', 'unmalted'],
  }),
  craftMalt({
    id: 'solstice-raw-corn',
    name: 'Raw Corn',
    maker: 'Solstice Malt',
    type: 'adjunct-grain',
    maltClass: 'non-buffering',
    extractFineDryPercent: 80.0,
    moisturePercent: 11.0,
    colorLovibond: 1.0,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 25,
    needsMashing: true,
    description:
      'Unprocessed corn offered alongside the malted range, giving body and gentle sweetness. Raw grain needs a cereal mash or a strongly diastatic base malt to convert, and rice hulls help with the sticky ones. Solstice publish product notes but no analysis; extract, moisture and diastatic figures here are class typicals.',
    tags: ['adjunct', 'raw-grain', 'corn', 'utah', 'unmalted'],
  }),
  craftMalt({
    id: 'solstice-raw-oats',
    name: 'Raw Oats',
    maker: 'Solstice Malt',
    type: 'adjunct-grain',
    maltClass: 'non-buffering',
    extractFineDryPercent: 70.0,
    moisturePercent: 11.0,
    colorLovibond: 1.5,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 20,
    needsMashing: true,
    description:
      'Unprocessed oats offered alongside the malted range, giving creaminess and texture. Raw grain needs a cereal mash or a strongly diastatic base malt to convert, and rice hulls help with the sticky ones. Solstice publish product notes but no analysis; extract, moisture and diastatic figures here are class typicals.',
    tags: ['adjunct', 'raw-grain', 'oats', 'utah', 'unmalted'],
  }),
  craftMalt({
    id: 'solstice-raw-rye',
    name: 'Raw Rye (Organic)',
    maker: 'Solstice Malt',
    type: 'adjunct-grain',
    maltClass: 'non-buffering',
    extractFineDryPercent: 74.0,
    moisturePercent: 11.0,
    colorLovibond: 2.8,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 12,
    needsMashing: true,
    description:
      'Unprocessed rye offered alongside the malted range, giving an earthy, robust rye edge. Raw grain needs a cereal mash or a strongly diastatic base malt to convert, and rice hulls help with the sticky ones. Solstice publish product notes but no analysis; extract, moisture and diastatic figures here are class typicals.',
    tags: ['adjunct', 'raw-grain', 'rye', 'utah', 'unmalted'],
  }),
  craftMalt({
    id: 'solstice-raw-wheat',
    name: 'Raw Wheat',
    maker: 'Solstice Malt',
    type: 'adjunct-grain',
    maltClass: 'non-buffering',
    extractFineDryPercent: 78.0,
    moisturePercent: 11.0,
    colorLovibond: 1.6,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 25,
    needsMashing: true,
    description:
      'Unprocessed wheat offered alongside the malted range, giving body and traditional witbier character. Raw grain needs a cereal mash or a strongly diastatic base malt to convert, and rice hulls help with the sticky ones. Solstice publish product notes but no analysis; extract, moisture and diastatic figures here are class typicals.',
    tags: ['adjunct', 'raw-grain', 'wheat', 'utah', 'unmalted'],
  }),

  // ---------------------------------------------------------------------------
  // Mountain Malt — Idaho Falls, Idaho
  //
  // A family farm-and-malthouse on the Snake River Plain that makes base malt
  // only. Every figure comes from their 2025 spec sheet (the PDF linked from
  // `mountainmalt.com/our-products`). It gives limits, not lot analyses:
  // moisture < 4.5 %, DP > 120, extract FG > 81 % (> 80 % for Vienna), and a
  // colour band. The rows use the limit for moisture, DP and extract and the
  // midpoint of the colour band. Like Montana Craft Malt, that makes them
  // `representative`, not `spec-sheet`.
  //
  // The sheet gives Vienna's moisture as "> 4.5 %". That reads like a flipped
  // sign, but it is what they print, so the row uses 4.5 either way. Their
  // product page calls the malts "Pils", "2-Row Brewers' Malt" and "Vienna
  // Malt"; the sheet calls them "Pilsner 2-Row", "Standard 2-Row" and
  // "Mountain Vienna". The names below use the page, except for Vienna, where
  // the sheet's name is the more specific one.
  //
  // Whiskey Malt is left out. The page describes it as European low-GN
  // varieties "tailored to our customers requests", which is a service rather
  // than a fixed product, and it gives no figures.
  //
  // One products page and one PDF cover all three malts, so no row has a `url`.
  // ---------------------------------------------------------------------------
  craftMalt({
    id: 'mountain-malt-pilsner',
    name: 'Pilsner Malt',
    maker: 'Mountain Malt',
    type: 'base',
    maltClass: 'base-pilsner',
    extractFineDryPercent: 81,
    moisturePercent: 4.5,
    colorLovibond: 1.6,
    diastaticPowerLintner: 120,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'Crisp, clean pilsner malt from eastern Idaho two-row, light straw with a delicate malt note. It works as the whole grist of a lager or a pale ale. Figures are the maker’s published spec limits.',
    tags: ['base', 'pilsner', 'lager', 'craft-malt', 'idaho', 'local'],
  }),
  craftMalt({
    id: 'mountain-malt-2-row',
    name: '2-Row Brewers’ Malt',
    maker: 'Mountain Malt',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 81,
    moisturePercent: 4.5,
    colorLovibond: 2,
    diastaticPowerLintner: 120,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'Their standard two-row: malty, sweet and clean, straw with golden hues, and enzymatic enough to carry adjuncts. An everyday base for any style. Figures are the maker’s published spec limits.',
    tags: ['base', 'pale', '2-row', 'craft-malt', 'idaho', 'local'],
  }),
  craftMalt({
    id: 'mountain-malt-vienna',
    name: 'Mountain Vienna',
    maker: 'Mountain Malt',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 80,
    moisturePercent: 4.5,
    colorLovibond: 3.3,
    diastaticPowerLintner: 120,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'A pale Vienna with honey and light nut flavors that gives golden to light orange color. With over 120 °Lintner it can be the whole grist of a Vienna lager, or 20 to 40 percent of an ale for more malt depth. Figures are the maker’s published spec limits.',
    tags: ['kilned', 'vienna', 'craft-malt', 'idaho', 'local'],
  }),

  // ---------------------------------------------------------------------------
  // Schwebach Malz — near Estancia (McIntosh), New Mexico
  //
  // New Mexico's first malthouse. Schwebach's LLC is a Torrance County forage
  // farm that began growing barley in 2017 and had it toll-malted in Wyoming.
  // In 2024 it opened its own plant on the drum system that came out of
  // Wyoming Malting in Pine Bluffs. The Craft Maltsters Guild lists it as
  // "Schwebach's LLC, McIntosh"; the malt trades as Schwebach Malz.
  //
  // **These names come from a single third party.** The maker publishes
  // nothing: `schwebachmalts.com` is a hosting "Coming Soon" page. The three
  // products below are named in a February 2024 launch piece by the Dark Side
  // Brew Crew (nmdarksidebrewcrew.com), quoting Ryan Schwebach: Estancia Valley
  // Pilsner, a distiller's malt and a Munich. That piece also names a New
  // Mexico English Pale, but only "pending a good COA", so it is left out
  // until something shows it shipped. Local brewers still using the malt in
  // 2026 (Santa Fe Brewing, La Cumbre's Schwebach Lager) confirm the house is
  // operating and that the pilsner exists. They say nothing about the other
  // two malts.
  //
  // All figures are class-typical and every description says so. Do not add
  // "estate", "heritage" or a barley variety: none is published.
  // ---------------------------------------------------------------------------
  craftMalt({
    id: 'schwebach-estancia-valley-pilsner',
    name: 'Estancia Valley Pilsner',
    maker: 'Schwebach Malz',
    type: 'base',
    maltClass: 'base-pilsner',
    extractFineDryPercent: 80,
    moisturePercent: 4.5,
    colorLovibond: 1.8,
    diastaticPowerLintner: 90,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'Pilsner malt from barley grown in the Estancia Valley and malted there, the base of several all-New Mexico lagers. All values shown are class-typical, since the maker publishes no analysis.',
    tags: ['base', 'pilsner', 'lager', 'craft-malt', 'new-mexico', 'local'],
  }),
  craftMalt({
    id: 'schwebach-munich',
    name: 'Munich Malt',
    maker: 'Schwebach Malz',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 78,
    moisturePercent: 4,
    colorLovibond: 8,
    diastaticPowerLintner: 50,
    suggestedMaxPercent: 60,
    needsMashing: true,
    description:
      'New Mexico-grown Munich malt for bready, toasty depth in ambers, bocks and malt-forward lagers. All values shown are class-typical, since the maker lists the malt by name only.',
    tags: ['kilned', 'munich', 'malty', 'craft-malt', 'new-mexico', 'local'],
  }),
  craftMalt({
    id: 'schwebach-distillers',
    name: 'Distiller’s Malt',
    maker: 'Schwebach Malz',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 79,
    moisturePercent: 4.5,
    colorLovibond: 1.8,
    diastaticPowerLintner: 140,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'A lightly kilned, enzyme-rich malt for craft distillers converting corn, rye or wheat alongside it. All values shown are class-typical, since the maker lists the malt by name only.',
    tags: ['base', 'distilling', 'high-dp', 'craft-malt', 'new-mexico', 'local'],
  }),
];
